import axios from 'axios';
import { config } from '../config';

// Configure axios defaults
axios.defaults.xsrfCookieName = 'csrftoken';
axios.defaults.xsrfHeaderName = 'X-CSRFToken';
axios.defaults.withCredentials = true;

// Create an axios instance with default config
console.log(config.backend.host);
const apiClient = axios.create({
  baseURL: config.backend.host ,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
  xsrfCookieName: 'csrftoken',
  xsrfHeaderName: 'X-CSRFToken',
});

// Function to set auth token in headers
export const setAuthToken = (token) => {
  if (token && token !== 'null' && token !== 'undefined') {
    const formattedToken = token.trim();
    axios.defaults.headers.common['Authorization'] = formattedToken;
    apiClient.defaults.headers.common['Authorization'] = formattedToken;
    localStorage.setItem('auth_token', formattedToken);
    return true;
  } else {
    delete axios.defaults.headers.common['Authorization'];
    delete apiClient.defaults.headers.common['Authorization'];
    return false;
  }
};

// Initialize API with token from localStorage on load
(() => {
  const token = localStorage.getItem('auth_token');
  if (token && token !== 'null' && token !== 'undefined') {
    setAuthToken(token);
  }
})();

// Function to refresh CSRF token if needed
export const refreshCSRFToken = async (isBdaRequest = false) => {
  try {
    const response = await apiClient.get(
      isBdaRequest ? '/api/bda/auth/csrf/' : '/api/admin/auth/get_csrf_token/',
      { 
        withCredentials: true,
        headers: {
          'X-CSRFToken': document.cookie.match(/csrftoken=([^;]+)/)?.[1] || ''
        }
      }
    );
    return response.data.token;
  } catch (error) {
    console.error('Error refreshing CSRF token:', error);
    throw error;
  }
};

// Add a request interceptor to handle CSRF token
apiClient.interceptors.request.use(
  async (config) => {
    const url = typeof config.url === 'string' ? config.url : String(config.url || '');
    const requestType = url.includes('/api/bda/') ? 'BDA' : 
                      (url.includes('/api/sp/') ? 'Service Provider' : 'Admin');
    
    // Get CSRF token from cookie
    const csrfToken = document.cookie.match(/csrftoken=([^;]+)/)?.[1];
    
    // Add CSRF token to headers if it exists
    if (csrfToken) {
      config.headers['X-CSRFToken'] = csrfToken;
    }
    
    // Add auth token if exists
    const token = localStorage.getItem('auth_token');
    if (token && token !== 'null' && token !== 'undefined') {
      const formattedToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
      config.headers.Authorization = formattedToken;
    }
    
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    
    config.params = { 
      ...config.params,
      _t: new Date().getTime() 
    };
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    if (!error.config) {
      return Promise.reject(error);
    }
    
    const originalRequest = error.config;
    const url = typeof originalRequest.url === 'string' ? originalRequest.url : String(originalRequest.url || '');
    const method = originalRequest.method?.toUpperCase() || 'METHOD';
    
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const userRole = localStorage.getItem('userRole');
    const userType = userData.user_type || (userRole === 'bda' ? 'bda' : 
                                           (userRole === 'service-provider' || userRole === 'sp_staff') ? 'sp' : 'admin');
    
    if ((error.response?.data?.detail?.includes('CSRF') || error.response?.data?.error?.includes('CSRF')) && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        const isBdaRequest = userType === 'bda' || url.includes('/api/bda/');
        await refreshCSRFToken(isBdaRequest);
        return apiClient(originalRequest);
      } catch (e) {
        return Promise.reject(e);
      }
    }
    
    if (error.response?.status === 401) {
      window.authFailureDetected = true;
      window.authFailureUserType = userType;
      
      if ((userType === 'bda' || url.includes('/api/bda/')) && 
          !url.includes('/login') && 
          !originalRequest._retry && 
          localStorage.getItem('refresh_token')) {
        
        originalRequest._retry = true;
        
        try {
          const refreshToken = localStorage.getItem('refresh_token');
          const response = await axios.post('/api/bda/auth/refresh/', { refresh: refreshToken });
          
          if (response.data.access) {
            const newToken = `Bearer ${response.data.access}`;
            localStorage.setItem('auth_token', newToken);
            originalRequest.headers['Authorization'] = newToken;
            return apiClient(originalRequest);
          }
        } catch (refreshError) {
          localStorage.removeItem('auth_token');
          localStorage.removeItem('refresh_token');
          
          if (userType === 'bda') {
            window.location.href = '/bda/login';
          } else if (userType === 'sp') {
            window.location.href = '/sp/login';
          } else {
            window.location.href = '/login';
          }
        }
      }
    }
    
    return Promise.reject(error);
  }
);

// Pagination utilities
export const paginationUtils = {
  // Extract pagination info from response
  getPaginationInfo: (response) => {
    // Handle Django REST Framework style pagination
    if (response.data && typeof response.data === 'object') {
      // DRF with pagination
      if ('count' in response.data && ('results' in response.data || 'items' in response.data)) {
        const results = response.data.results || response.data.items;
        return {
          data: results,
          totalCount: response.data.count,
          next: response.data.next,
          previous: response.data.previous,
          currentPage: response.data.current_page || 1,
          totalPages: response.data.total_pages || Math.ceil(response.data.count / (results?.length || 10)),
          pageSize: results?.length || 10
        };
      }
      
      // Headers-based pagination (often used with DRF)
      if (response.headers && (response.headers['x-total-count'] || response.headers['content-range'])) {
        const totalCount = response.headers['x-total-count'] 
          ? parseInt(response.headers['x-total-count'], 10)
          : parseInt(response.headers['content-range']?.split('/')[1], 10);
          
        const pageSize = response.data.length;
        const totalPages = Math.ceil(totalCount / pageSize);
        
        return {
          data: response.data,
          totalCount,
          totalPages,
          pageSize
        };
      }
    }
    
    // Not paginated or custom pagination
    return {
      data: response.data,
      totalCount: response.data.length,
      totalPages: 1,
      currentPage: 1,
      pageSize: response.data.length
    };
  },
  
  // Make a paginated API request
  getPaginatedData: async (url, page = 1, pageSize = 10, filters = {}) => {
    try {
      // Construct query params for pagination
      const params = {
        page,
        page_size: pageSize,
        ...filters
      };
      
      // Remove any undefined or null values from params
      Object.keys(params).forEach(key => {
        if (params[key] === undefined || params[key] === null) {
          delete params[key];
        }
      });
      
      const response = await apiClient.get(url, { params });
      return paginationUtils.getPaginationInfo(response);
    } catch (error) {
      console.error("Error fetching paginated data:", error);
      throw error;
    }
  },
  
  // Infinite scroll helper - fetches next page of data
  getNextPage: async (nextPageUrl, filters = {}) => {
    if (!nextPageUrl) return null;
    
    try {
      // If nextPageUrl is a full URL, use axios directly
      if (nextPageUrl.startsWith('http')) {
        const response = await axios.get(nextPageUrl, {
          headers: apiClient.defaults.headers,
          params: filters
        });
        return paginationUtils.getPaginationInfo(response);
      }
      
      // Otherwise use our apiClient
      const response = await apiClient.get(nextPageUrl, { params: filters });
      return paginationUtils.getPaginationInfo(response);
    } catch (error) {
      console.error("Error fetching next page:", error);
      throw error;
    }
  }
};

// Authentication API
export const authAPI = {
  login: (credentials) => {
    // Get CSRF token from cookie
    const csrfToken = document.cookie.match(/csrftoken=([^;]+)/)?.[1];
    
    return apiClient.post('/api/admin/auth/login/', credentials, {
      headers: {
        'X-CSRFToken': csrfToken || ''
      },
      withCredentials: true
    });
  },
  logout: () => apiClient.post('/api/admin/auth/logout/'),
  refreshToken: (data) => apiClient.post('/api/admin/auth/refresh_token/', data),
  me: () => {
    // Get token from localStorage
    const token = localStorage.getItem('auth_token');
    // Create config with explicit auth header
    const config = {
      headers: {
        'Authorization': token // Token already includes 'Bearer ' prefix
      }
    };
    return apiClient.get('/api/admin/auth/me/', config);
  },
  changePassword: (data) => apiClient.put('/api/admin/auth/change-password/', data),
  resetPassword: (data) => apiClient.post('/api/admin/auth/reset-password/', data),
  refreshCSRFToken: async () => {
    try {
      console.log('Refreshing admin CSRF token...');
      const response = await apiClient.get('/api/admin/auth/get_csrf_token/', { 
        withCredentials: true 
      });
      console.log('Admin CSRF token refreshed');
      return response.data.csrfToken;
    } catch (error) {
      console.error('Failed to refresh admin CSRF token:', error);
      throw error;
    }
  },
};

// Service Provider Authentication API
export const serviceProviderAuthAPI = {
  register: (data) => {
    // For multipart/form-data with files
    if (data instanceof FormData) {
      const config = {
        headers: {
          'Content-Type': undefined
        }
      };
      return apiClient.post('/api/provider/register/', data, config);
    }
    return apiClient.post('/api/provider/register/', data);
  },
  login: (credentials) => apiClient.post('/api/sp/auth/login/', credentials),
  logout: () => apiClient.post('/api/sp/auth/logout/'),
  changeTemporaryPassword: (data) => {
    // Get the auth token from localStorage
    const authToken = localStorage.getItem('auth_token');
    
    // Create request configuration with token if available
    const config = {};
    if (authToken) {
      config.headers = {
        'Authorization': authToken
      };
    }
    
    // Make the API call
    return apiClient.post('/api/sp/reset-temp-password/', {
      email: localStorage.getItem('email') || data.email,
      temp_password: data.temp_password,
      new_password: data.new_password,
      terms_accepted: data.terms_accepted
    }, config).then(response => {
      // If the response contains a new token, set it
      if (response.data && response.data.token) {
        const token = response.data.token;
        const formattedToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
        setAuthToken(formattedToken);
      }
      return response;
    });
  },
  refreshCSRFToken: async () => {
    try {
      console.log('Refreshing service provider CSRF token...');
      await axios.get('/api/sp/auth/csrf/', { withCredentials: true });
      console.log('Service provider CSRF token refreshed');
    } catch (error) {
      console.error('Failed to refresh service provider CSRF token:', error);
      throw error;
    }
  },
  getProfile: () => apiClient.get('/api/sp/auth/me/'),
  updateProfile: (data) => {
    if (data instanceof FormData) {
      const config = {
        headers: {
          'Content-Type': undefined
        }
      };
      return apiClient.patch('/api/sp/auth/profile/', data, config);
    }
    return apiClient.patch('/api/sp/auth/profile/', data);
  }
};

// BDA API
export const bdaAuthAPI = {
  login: (data) => apiClient.post('/api/bda/auth/login/', data),
  logout: () => apiClient.post('/api/bda/auth/logout/'),
  getProfile: () => apiClient.get('/api/bda/profile/'),
  getDetailedProfile: () => apiClient.get('/api/bda/profile/detailed/'),
  getApplicationStatus: () => apiClient.get('/api/bda/auth/status-check/'),
  getRevisionDetails: () => apiClient.get('/api/bda/profile/revision-details/'),
  submitRevision: (data) => apiClient.post('/api/bda/profile/submit_revision/', data),
  refreshCSRFToken: async () => {
    try {
      console.log('Refreshing BDA CSRF token...');
      await apiClient.get('/api/bda/auth/csrf/', { withCredentials: true });
      console.log('BDA CSRF token refreshed');
    } catch (error) {
      console.error('Failed to refresh BDA CSRF token:', error);
      throw error;
    }
  },
  changePassword: async (data) => {
    try {
      const response = await apiClient.post('/api/bda/profile/change_password/', data
    );
      return response.data;
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },
};

// Add BDA specialty API
export const bdaSpecialtyAPI = {
  getAll: (page = 1, pageSize = 100, filters = {}) => 
    paginationUtils.getPaginatedData('/api/bda/specialties/', page, pageSize, filters),
  getOne: (id) => apiClient.get(`/api/bda/specialties/${id}/`)
};

// Add BDA symptom API
export const bdaSymptomAPI = {
  getAll: (page = 1, pageSize = 100, filters = {}) => 
    paginationUtils.getPaginatedData('/api/bda/symptoms/', page, pageSize, filters),
  getOne: (id) => apiClient.get(`/api/bda/symptoms/${id}/`),
  search: (query) => apiClient.get('/api/bda/symptoms/', { 
    params: { search: query } 
  })
};

// Add BDA provider type API
export const bdaProviderTypeAPI = {
  getAll: (page = 1, pageSize = 10, filters = {}) => 
    paginationUtils.getPaginatedData('/api/bda/provider-types/', page, pageSize, filters),
  getOne: (id) => apiClient.get(`/api/bda/provider-types/${id}/`)
};

export const bdaStatsAPI = {
  // For BDA users to view their own stats
  getMyStats: () => apiClient.get('/api/bda/statistics/'),
  getLeaderboard: (months = 6) => apiClient.get(`/api/bda/leaderboard/?months=${months}`),
  getMyCommissionTier: () => apiClient.get('/api/bda/monthly-sales/').then(response => {
    if (response.data && response.data.summary && response.data.summary.current_month) {
      return response.data.summary.current_month;
    }
    return null;
  }),
  
  // For admins to view BDA stats
  getAllStats: (months = 12) => apiClient.get(`/api/admin/bda-stats/?months=${months}`),
  getBDAStats: (id, months = 12) => apiClient.get(`/api/admin/bda-stats/${id}/?months=${months}`),
  getBDACommissionTier: (id) => apiClient.get('/api/admin/bda-monthly-sales/summary/', { 
    params: { bda_id: id } 
  }).then(response => {
    if (response.data && response.data.summary && response.data.summary.current_month) {
      return response.data.summary.current_month;
    }
    return null;
  }),
  getLeaderboard: (months = 6) => apiClient.get(`/api/admin/bda-leaderboard/?months=${months}`),
};

export const bdaServiceProviderAPI = {
  // Get list of service providers
  getBDAServiceProviders: (params = {}) => {
    return apiClient.get('/api/bda/service-providers/', { params });
  },

  // Get details of a specific service provider
  getBDAServiceProviderDetails: (providerId) => {
    return apiClient.get(`/api/bda/service-providers/${providerId}/`);
  },

  // Create a new service provider
  createServiceProvider: (data) => {
    return apiClient.post('/api/bda/service-providers/', data, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },

  // Update a service provider
  updateServiceProvider: (providerId, data) => {
    return apiClient.put(`/api/bda/service-providers/${providerId}/update/`, data, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }
};

export const bdaEasyOPPatientAPI = {
  getAll: (page = 1, pageSize = 10, filters = {}) => 
    paginationUtils.getPaginatedData('/api/bda/patients/easyop/', page, pageSize, filters),
  getOne: (id) => apiClient.get(`/api/bda/patients/easyop/${id}/`),
  update: (id, data) => {
    // For multipart/form-data with files
    if (data instanceof FormData) {
      const config = {
        headers: {
          'Content-Type': undefined
        }
      };
      
      return apiClient.put(`/api/bda/patients/easyop/${id}/`, data, config);
    }
    
    return apiClient.put(`/api/bda/patients/easyop/${id}/`, data);
  },
  patch: (id, data) => apiClient.patch(`/api/bda/patients/easyop/${id}/`, data),
};

// Dashboard API
export const dashboardAPI = {
  getSummary: () => apiClient.get('/api/admin/dashboard/summary/').then(response => {
    // Return the data directly since it's already in the correct format
    return response.data;
  }),
  getAppointmentStats: () => apiClient.get('/api/admin/dashboard/appointments/'),
  getRevenueStats: () => apiClient.get('/api/admin/dashboard/revenue/'),
  getUserGrowth: () => apiClient.get('/api/admin/dashboard/user_growth/'),
  getRecentProviders: () => apiClient.get('/api/admin/dashboard/recent-providers/'),
  getRecentBDAs: () => apiClient.get('/api/admin/dashboard/recent-bdas/'),
  getRecentTickets: () => apiClient.get('/api/admin/dashboard/recent-tickets/'),
};

// Specialty API
export const specialtyAPI = {
  getAll: (page = 1, pageSize = 10, filters = {}) => 
    paginationUtils.getPaginatedData('/api/admin/specialties/', page, pageSize, filters),
  getOne: (id) => apiClient.get(`/api/admin/specialties/${id}/`),
  create: (data) => apiClient.post('/api/admin/specialties/', data),
  update: (id, data) => apiClient.put(`/api/admin/specialties/${id}/`, data),
  delete: (id) => apiClient.delete(`/api/admin/specialties/${id}/`),
};

// Symptom API
export const symptomAPI = {
  getAll: (page = 1, pageSize = 10, filters = {}) => 
    paginationUtils.getPaginatedData('/api/admin/symptoms/', page, pageSize, filters),
  getOne: (id) => apiClient.get(`/api/admin/symptoms/${id}/`),
  create: (data) => apiClient.post('/api/admin/symptoms/', data),
  update: (id, data) => apiClient.put(`/api/admin/symptoms/${id}/`, data),
  delete: (id) => apiClient.delete(`/api/admin/symptoms/${id}/`),
  search: (query) => apiClient.get('/api/admin/symptoms/', { 
    params: { search: query } 
  }),
};

// Qualifications API
export const qualificationsAPI = {
  getAll: (page = 1, pageSize = 10, filters = {}) => 
    paginationUtils.getPaginatedData('/api/user/qualifications/', page, pageSize, filters),
  getOne: (id) => apiClient.get(`/api/user/qualifications/${id}/`),
  create: (data) => apiClient.post('/api/user/qualifications/', data),
  update: (id, data) => apiClient.put(`/api/user/qualifications/${id}/`, data),
  delete: (id) => apiClient.delete(`/api/user/qualifications/${id}/`),
};

// Service Provider Type API
export const serviceProviderTypeAPI = {
  getAll: (page = 1, pageSize = 10, filters = {}) => 
    paginationUtils.getPaginatedData('/api/admin/provider-types/', page, pageSize, filters),
  getOne: (id) => apiClient.get(`/api/admin/provider-types/${id}/`),
  create: (data) => apiClient.post('/api/admin/provider-types/', data),
  update: (id, data) => apiClient.put(`/api/admin/provider-types/${id}/`, data),
  delete: (id) => apiClient.delete(`/api/admin/provider-types/${id}/`)
};

// Service Provider Type Organization API
export const serviceProviderTypeOrgAPI = {
  getAll: (page = 1, pageSize = 10, filters = {}) => 
    paginationUtils.getPaginatedData('/api/admin/provider-organizations/', page, pageSize, filters),
  getOne: (id) => apiClient.get(`/api/admin/provider-organizations/${id}/`),
  create: (data) => apiClient.post('/api/admin/provider-organizations/', data),
  update: (id, data) => apiClient.put(`/api/admin/provider-organizations/${id}/`, data),
  delete: (id) => apiClient.delete(`/api/admin/provider-organizations/${id}/`),
  toggleActive: (id) => apiClient.post(`/api/admin/provider-organizations/${id}/toggle_active/`)
};

// Service Provider API
export const serviceProviderAPI = {
  // Get all service providers with pagination
  getAll: async (page = 1, pageSize = 20, filters = {}) => {
    // Build query parameters - explicitly include all possible pagination parameters
    const params = {
      page,
      page_size: pageSize, // Django REST Framework parameter
      limit: pageSize, // Alternative parameter some APIs use
      offset: (page - 1) * pageSize, // For offset-based pagination
      ...filters,
      _t: new Date().getTime() // Add timestamp to prevent caching
    };

    try {
      console.log(`Fetching service providers: page=${page}, pageSize=${pageSize}`);
      const response = await apiClient.get('/api/admin/service-providers/', { params });
      
      // Check if the response is paginated (DRF format)
      if (response.data && typeof response.data === 'object' && 'results' in response.data) {
        // Django REST Framework paginated response
        return {
          data: response.data.results,
          count: response.data.count,
          next: response.data.next,
          previous: response.data.previous,
          totalPages: Math.ceil(response.data.count / pageSize),
          currentPage: page
        };
      } 
      // Handle non-paginated array response
      else if (Array.isArray(response.data)) {
        console.warn('Backend returned non-paginated data - pagination might not be active');
        return {
          data: response.data,
          count: response.data.length,
          next: null,
          previous: null,
          totalPages: 1,
          currentPage: 1
        };
      }
      // Handle other object response (non-standard)
      else if (response.data && typeof response.data === 'object') {
        console.warn('Received unexpected response format:', response.data);
        return {
          data: response.data,
          count: 0,
          next: null,
          previous: null,
          totalPages: 1,
          currentPage: 1
        };
      }
      
      // Fallback for any other format
      return {
        data: [],
        count: 0,
        next: null,
        previous: null,
        totalPages: 1,
        currentPage: 1
      };
    } catch (error) {
      console.error('Error fetching service providers:', error);
      throw error;
    }
  },
  
  // Get a single service provider by ID
  getOne: async (id) => {
    const response = await apiClient.get(`/api/admin/service-providers/${id}/`);
    return response;
  },
  sendOTP: (data) => apiClient.post('/api/admin/service-provider/send-otp/', data),
verifyOTP: (data) => apiClient.post('/api/admin/service-provider/verify-otp/', data),
  // Create a new service provider
  create: async (data) => {
    const response = await apiClient.post('/api/admin/service-providers/', data);
    return response;
  },
  
  // Create a service provider with FormData (for file uploads)
  createWithFormData: async (formData) => {
    try {
      console.log('Creating service provider with FormData:', formData);
      
      // Log FormData contents for debugging
      for (let [key, value] of formData.entries()) {
        console.log(`${key}:`, value instanceof File ? `File: ${value.name}` : value);
      }

      const response = await apiClient.post('/api/admin/service-providers/create_provider/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      return response;
    } catch (error) {
      console.error('Error creating service provider:', error);
      throw error;
    }
  },
  
  // Update a service provider
  update: async (id, data) => {
    const response = await apiClient.put(`/api/admin/service-providers/${id}/`, data);
    return response;
  },
  
  // Partially update a service provider
  patch: async (id, data) => {
    const response = await apiClient.patch(`/api/admin/service-providers/${id}/update_provider/`, data);
    return response;
  },
  
  // Delete a service provider
  delete: async (id) => {
    const response = await apiClient.delete(`/api/admin/service-providers/${id}/`);
    return response;
  },
  
  // Toggle a user's active status (for deactivating service provider users)
  toggleActiveStatus: async (userId) => {
    const response = await apiClient.post(`/api/admin/users/${userId}/toggle_active_status/`);
    return response;
  },
  
  // Get specialties for a service provider
  getSpecialties: async (id) => {
    const response = await apiClient.get(`/api/admin/service-providers/${id}/specialties/`);
    return response;
  },
  
  // Get doctors for a service provider
  getDoctors: async (id) => {
    const response = await apiClient.get(`/api/admin/service-providers/${id}/doctors/`);
    return response;
  },
  
  // Get platform details (BookMyOP and EasyOP) for a service provider
  getPlatformDetails: async (id, filters = {}) => {
    const response = await apiClient.get(`/api/admin/service-providers/${id}/platform_details/`, {
      params: filters
    });
    return response;
  },
  
  // Submit a service provider for approval
  submit: async (id) => {
    const response = await apiClient.post(`/api/admin/service-providers/${id}/submit/`);
    return response;
  },
  
  // Approve a service provider
  approve: async (id) => {
    const response = await apiClient.post(`/api/admin/service-providers/${id}/approve/`);
    return response;
  },
  
  // Reject a service provider
  reject: async (id) => {
    const response = await apiClient.post(`/api/admin/service-providers/${id}/reject/`);
    return response;
  },
  
  // Update service provider status 
  updateStatus: async (id, data) => {
    const endpoint = data.status === 'approved' ? 'approve' : 
                     data.status === 'rejected' ? 'reject' : 'submit';
    const response = await apiClient.post(`/api/admin/service-providers/${id}/${endpoint}/`, data);
    return response;
  },
  
  // Toggle status - new endpoint that allows changing to any status
  toggleStatus: async (id, data) => {
    // Convert data to FormData
    const formData = new FormData();
    
    // Add all properties from data object to FormData
    Object.keys(data).forEach(key => {
      formData.append(key, data[key]);
    });
    
    const response = await apiClient.post(`/api/admin/service-providers/${id}/toggle_status/`, formData);
    return response;
  },
  
  // Add a specialty to a service provider
  addSpecialty: async (id, data) => {
    const response = await apiClient.post(`/api/admin/service-providers/${id}/add_specialty/`, data);
    return response;
  },

  // Add specialty to a service provider
  addSpecialty: async (providerId, specialtyData) => {
    try {
      const response = await apiClient.post(`/api/admin/service-providers/${providerId}/add_specialty/`, specialtyData);
      return response;
    } catch (error) {
      console.error('Error adding specialty:', error);
      throw error;
    }
  },

  // Update specialty for a service provider
  updateSpecialty: async (providerId, specialtyId, specialtyData) => {
    try {
      const response = await apiClient.put(`/api/admin/provider-specialties/${specialtyId}/`, specialtyData);
      return response;
    } catch (error) {
      console.error('Error updating specialty:', error);
      throw error;
    }
  },

  // Add doctor to a service provider
  addDoctor: async (providerId, doctorData) => {
    try {
      // Create FormData object
      const formData = new FormData();
      
      // Add all fields to FormData
      Object.keys(doctorData).forEach(key => {
        if (key !== 'profile_photo' && key !== 'available_slots') {
          formData.append(key, doctorData[key]);
        }
      });

      // Add profile photo if exists
      if (doctorData.profile_photo) {
        formData.append('profile_photo', doctorData.profile_photo);
      }

      // Add available slots as JSON string
      if (doctorData.available_slots) {
        formData.append('available_slots', JSON.stringify(doctorData.available_slots));
      }

      // Debug log
      console.log('Sending doctor data:', {
        providerId,
        formData: Object.fromEntries(formData.entries())
      });

      const response = await apiClient.post(`/api/admin/service-providers/${providerId}/doctors/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response;
    } catch (error) {
      console.error('Error adding doctor:', error);
      throw error;
    }
  },

  // Update doctor for a service provider
  updateDoctor: async (providerId, doctorId, doctorData) => {
    const formData = new FormData();
    
    Object.keys(doctorData).forEach(key => {
      if (key !== 'profile_photo' && key !== 'available_slots') {
        formData.append(key, doctorData[key]);
      }
    });

    if (doctorData.profile_photo) {
      formData.append('profile_photo', doctorData.profile_photo);
    }

    if (doctorData.available_slots) {
      formData.append('available_slots', JSON.stringify(doctorData.available_slots));
    }

    try {
      const response = await apiClient.patch(`/api/admin/doctors/${doctorId}/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response;
    } catch (error) {
      console.error('Error updating doctor:', error);
      throw error;
    }
  },

  // Update doctor star rating
  updateDoctorStarRating: async (doctorId, starRating) => {
    try {
      const formData = new FormData();
      formData.append('star_rating', starRating);
      
      const response = await apiClient.patch(`/api/admin/doctors/${doctorId}/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response;
    } catch (error) {
      console.error('Error updating doctor star rating:', error);
      throw error;
    }
  },
  
  // Schedule management
  createSchedule: async (data) => {
    const response = await apiClient.post('/api/admin/schedules/', data);
    return response;
  },
  
  updateSchedule: async (id, data) => {
    const response = await apiClient.put(`/api/admin/schedules/${id}/`, data);
    return response;
  },
  
  deleteSchedule: async (id) => {
    const response = await apiClient.delete(`/api/admin/schedules/${id}/`);
    return response;
  },

  // Doctor related methods
  getDoctors: async (serviceProviderId, params = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(key, value);
      }
    });
    const response = await apiClient.get(`/api/admin/service-providers/${serviceProviderId}/doctors/?${queryParams.toString()}`);
    return response;
  },

  addDoctor: async (serviceProviderId, doctorData) => {
    const response = await apiClient.post(`/api/admin/service-providers/${serviceProviderId}/doctors/`, doctorData);
    return response;
  },

  updateDoctor: async (doctorId, doctorData) => {
    const response = await apiClient.patch(`/api/admin/doctors/${doctorId}/`, doctorData);
    return response;
  },

  deleteDoctor: async (doctorId) => {
    const response = await apiClient.delete(`/api/admin/doctors/${doctorId}/`);
    return response;
  },

  // Doctor Schedule APIs
  getDoctorSchedules: async (params = {}) => {
    return apiClient.get('/api/admin/doctor-schedules/', { params });
  },

  createDoctorSchedule: async (data) => {
    return apiClient.post('/api/admin/doctor-schedules/', data);
  },

  getDoctorSchedule: async (id) => {
    return apiClient.get(`/api/admin/doctor-schedules/${id}/`);
  },

  updateDoctorSchedule: async (id, data) => {
    return apiClient.put(`/api/admin/doctor-schedules/${id}/`, data);
  },

  deleteDoctorSchedule: async (id) => {
    return apiClient.delete(`/api/admin/doctor-schedules/${id}/`);
  },

  addTimeSlot: async (scheduleId, data) => {
    return apiClient.post(`/api/admin/doctor-schedules/${scheduleId}/add_time_slot/`, data);
  },

  removeTimeSlot: async (scheduleId, slotId) => {
    try {
      console.log('Removing time slot:', { scheduleId, slotId });
      const response = await apiClient.post(`/api/admin/doctor-schedules/${scheduleId}/remove-slot/`, {
        slot_id: slotId
      });
      console.log('Remove time slot API response:', response);
      return response.data;
    } catch (error) {
      console.error('Error in removeTimeSlot API call:', error);
      throw error;
    }
  },

  updateTimeSlot: async (scheduleId, slotId, data) => {
    return apiClient.post(`/api/admin/doctor-schedules/${scheduleId}/update_time_slot/`, {
      slot_id: slotId,
      ...data
    });
  },

  getSchedulesByDoctor: async (doctorId) => {
    return apiClient.get('/api/admin/doctor-schedules/by_doctor/', { params: { doctor_id: doctorId } });
  },

  getSchedulesByServiceProvider: async (serviceProviderId) => {
    return apiClient.get('/api/admin/doctor-schedules/by_service_provider/', { 
      params: { service_provider_id: serviceProviderId } 
    });
  },

  // BookMyOP Schedule APIs
  updateDoctorBookMyOPSchedule: async (doctorId, data) => {
    try {
      // If data is FormData, convert it to JSON
      let requestData;
      if (data instanceof FormData) {
        const timeSlots = JSON.parse(data.get('time_slots'));
        requestData = {
          is_active: data.get('is_active') === 'true',
          time_slots: timeSlots
        };
      } else {
        requestData = data;
      }

      const response = await apiClient.put(`/api/admin/doctors/${doctorId}/update_bookmyop_schedule/`, requestData);
      return response.data;
    } catch (error) {
      console.error('Error updating doctor schedule:', error);
      console.error('Error response:', error.response?.data);
      throw error;
    }
  },

  bulkUpdateDoctorsBookMyOPSchedules: async (formData) => {
    try {
      const response = await apiClient.post('/api/admin/doctors/bulk_update_bookmyop_schedules/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error updating doctor schedules:', error);
      console.error('Error response:', error.response?.data);
      throw error;
    }
  },

  // Get BookMyOP settings for a service provider
  getBookMyOpSettings: async (id) => {
    try {
      const response = await apiClient.get(`/api/admin/service-providers/${id}/book_my_op_settings/`);
      return response;
    } catch (error) {
      console.error('Error fetching BookMyOP settings:', error);
      throw error;
    }
  },

  // EasyOP methods
  getEasyOpSettings: async (serviceProviderId) => {
    const response = await apiClient.get(`/api/admin/service-providers/${serviceProviderId}/easy_op_settings/`);
    return response;
  },

  updateEasyOpSettings: async (serviceProviderId, settings) => {
    try {
      const response = await apiClient.put(`/api/admin/service-providers/${serviceProviderId}/easy_op_settings/`, settings);
      return response.data;
    } catch (error) {
      console.error('Error updating EasyOP settings:', error);
      throw error;
    }
  },

  updateDoctorEasyOpSchedule: async (scheduleId, scheduleData) => {
    try {
      const response = await apiClient.put(`/api/admin/doctor-schedules/${scheduleId}/update-easyop-schedule/`, scheduleData);
      return response.data;
    } catch (error) {
      console.error('Error updating doctor EasyOP schedule:', error);
      throw error;
    }
  },

  bulkUpdateDoctorsEasyOpSchedules: async (schedulesData) => {
    try {
      const response = await apiClient.post('/api/admin/doctor-schedules/bulk-update-easyop-schedules/', schedulesData);
      return response.data;
    } catch (error) {
      console.error('Error bulk updating doctors EasyOP schedules:', error);
      throw error;
    }
  },

  // Staff management methods
  getStaff: async (serviceProviderId) => {
    const response = await apiClient.get(`/api/admin/service-providers/${serviceProviderId}/staff/`);
    return response;
  },

  addStaff: async (serviceProviderId, staffData) => {
    const response = await apiClient.post(`/api/admin/service-providers/${serviceProviderId}/create_staff/`, staffData);
    return response;
  },

  updateStaff: async (serviceProviderId, staffId, staffData) => {
    const response = await apiClient.put(`/api/admin/service-providers/${serviceProviderId}/staff/${staffId}/`, staffData);
    return response;
  },

  deleteStaff: async (serviceProviderId, staffId) => {
    const response = await apiClient.delete(`/api/admin/service-providers/${serviceProviderId}/staff/${staffId}/`);
    return response;
  },

  // Add new methods for direct doctor time slot manipulation
  addDoctorTimeSlot: async (doctorId, slotData) => {
    try {
      const response = await apiClient.post(`/api/admin/doctors/${doctorId}/time_slots/`, slotData);
      return response;
    } catch (error) {
      console.error('Error adding doctor time slot:', error);
      throw error;
    }
  },
  bulkUpdateDoctorsBookMyOPSchedules: async (formData) => {
    try {
      const response = await apiClient.post(`/api/admin/doctors/bulk_update_bookmyop_slots/`, formData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error bulk updating doctors BookMyOP schedules:', error);
      throw error;
    }
  },
  bulkUpdateDoctorsEasyOpSchedules: async (formData) => {
    try {
      const response = await apiClient.post(`/api/admin/doctors/bulk_update_easyop_schedules/`, formData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error bulk updating doctors EasyOP schedules:', error);
      throw error;
    }
  },

  removeDoctorTimeSlot: async (doctorId, slotId) => {
    try {
      const response = await apiClient.delete(`/api/admin/doctors/${doctorId}/time_slots/${slotId}/`);
      return response.data;
    } catch (error) {
      console.error('Error removing doctor time slot:', error);
      throw error;
    }
  },
};

// Service Provider Specialty API
export const serviceProviderSpecialtyAPI = {
  getAll: (page = 1, pageSize = 10, filters = {}) => 
    paginationUtils.getPaginatedData('/api/admin/provider-specialties/', page, pageSize, filters),
  getOne: (id) => apiClient.get(`/api/admin/provider-specialties/${id}/`),
  create: (data) => {
    // Ensure we're sending the data with proper Content-Type
    console.log('Creating specialty with data:', JSON.stringify(data));
    
    // If data contains nested structures like doctors array, ensure it's properly formatted
    if (data.doctors && Array.isArray(data.doctors)) {
      console.log(`Sending ${data.doctors.length} doctors with the specialty`);
    }
    
    return apiClient.post('/api/admin/provider-specialties/', data, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
  },
  update: (id, data) => apiClient.put(`/api/admin/provider-specialties/${id}/`, data),
  delete: (id) => apiClient.delete(`/api/admin/provider-specialties/${id}/`),
};

// Doctor API
export const doctorAPI = {
  getAll: (page = 1, pageSize = 10, filters = {}) => {
    const params = { 
      page, 
      page_size: pageSize,
      ...filters 
    };
    
    // Add symptom filtering support
    if (filters.symptoms) {
      params.symptoms = Array.isArray(filters.symptoms) ? filters.symptoms.join(',') : filters.symptoms;
    }
    
    // Remove any undefined or null values from params
    Object.keys(params).forEach(key => {
      if (params[key] === undefined || params[key] === null) {
        delete params[key];
      }
    });
    
    return paginationUtils.getPaginatedData('/api/admin/doctors/', page, pageSize, params);
  },
  getOne: (id) => apiClient.get(`/api/admin/doctors/${id}/`),
  getOneWithSchedules: (id) => apiClient.get(`/api/admin/doctor-schedules/?doctor=${id}`),
  create: (data) => {
    if (data instanceof FormData) {
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      };
      return apiClient.post('/api/admin/doctors/', data, config);
    }
    return apiClient.post('/api/admin/doctors/', data);
  },
  update: (id, data) => {
    if (data instanceof FormData) {
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      };
      return apiClient.put(`/api/admin/doctors/${id}/`, data, config);
    }
    return apiClient.put(`/api/admin/doctors/${id}/`, data);
  },
  delete: (id) => apiClient.delete(`/api/admin/doctors/${id}/`),
  getSchedules: (id) => apiClient.get(`/api/admin/doctors/${id}/schedules/`),
  getAppointments: (id) => apiClient.get(`/api/admin/doctors/${id}/appointments/`),
  
  // Symptom-related methods
  updateSymptoms: (id, symptomIds) => apiClient.patch(`/api/admin/doctors/${id}/`, {
    area_of_expertise: symptomIds
  }),
  getSymptoms: (id) => apiClient.get(`/api/admin/doctors/${id}/`).then(response => 
    response.data.area_of_expertise || []
  ),
  addSymptoms: (id, symptomIds) => apiClient.patch(`/api/admin/doctors/${id}/`, {
    area_of_expertise: symptomIds
  }),
  removeSymptoms: (id, symptomIds) => apiClient.patch(`/api/admin/doctors/${id}/`, {
    area_of_expertise: symptomIds
  }),
};

// Doctor Schedule API
export const doctorScheduleAPI = {
  getAll: (page = 1, pageSize = 10, filters = {}) => 
    paginationUtils.getPaginatedData('/api/admin/doctor-schedules/', page, pageSize, filters),
  getOne: (id) => apiClient.get(`/api/admin/doctor-schedules/${id}/`),
  create: (data) => apiClient.post('/api/admin/doctor-schedules/', data),
  update: (id, data) => apiClient.put(`/api/admin/doctor-schedules/${id}/`, data),
  delete: (id) => apiClient.delete(`/api/admin/doctor-schedules/${id}/`),
};

// BDA API
export const bdaAPI = {
  getAll: (page = 1, limit = 10, filters = {}) => {
    // Make a copy of filters to avoid modifying the original object
    const params = { 
      page, 
      page_size: limit,  // Django REST Framework uses page_size instead of limit
      ...filters 
    };
    
    // Remove any undefined or null values from params
    Object.keys(params).forEach(key => {
      if (params[key] === undefined || params[key] === null) {
        delete params[key];
      }
    });
    
    // Log the API request parameters
    console.log('BDA API getAll request params:', params);
    
    return apiClient.get('/api/admin/bda/', { params })
      .then(response => {
        // Log the response for debugging
        console.log('BDA API getAll response:', response);
        
        // Return the response directly since it's already in Django REST Framework format
        return response;
      })
      .catch(error => {
        console.error('BDA API getAll error:', error.response?.data || error.message);
        throw  {
          message: error.response?.data?.error || error.message,
          status: error.response?.status,
          data: error.response?.data
        };
      });
  },
  
  getOne: (id) => {
    return apiClient.get(`/api/admin/bda/${id}/`);
  },
  sendOTP: async(data) => {
    // await refreshCSRFToken();
     return apiClient.post('/api/admin/bda-otp/send_otp/', data)},
verifyOTP: async(data) => {
  // await refreshCSRFToken();
  return apiClient.post('/api/admin/bda-otp/verify_otp/', data)},
  create: (formData) => {
    // If FormData is being sent, ensure Content-Type is properly set (or removed for multipart/form-data)
    if (formData instanceof FormData) {
      return apiClient.post('/api/admin/bda/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    }
    return apiClient.post('/api/admin/bda/', formData);
  },
  
  createBDA: (formData) => {
    // Use the dedicated create_bda endpoint
    if (formData instanceof FormData) {
      return apiClient.post('/api/admin/users/create_bda/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    }
    return apiClient.post('/api/admin/users/create_bda/', formData);
  },
  
  update: (id, formData) => {
    // If FormData is being sent, ensure Content-Type is properly set (or removed for multipart/form-data)
    if (formData instanceof FormData) {
      return apiClient.patch(`/api/admin/bda/${id}/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    }
    return apiClient.patch(`/api/admin/bda/${id}/`, formData);
  },
  
  delete: (id) => {
    return apiClient.delete(`/api/admin/bda/${id}/`);
  },
  
  updateStatus: (id, data) => {
    // Determine which endpoint to use based on the status
    let endpoint;
    switch (data.status) {
      case 'approved':
        endpoint = `/api/admin/bda/${id}/approve/`;
        
        // Convert data to FormData for approved case
        const approveFormData = new FormData();
        
        // Add any additional fields if needed
        if (data.notes) {
          approveFormData.append('comments', data.notes);
        }
        
        return apiClient.post(endpoint, approveFormData);
        break;
      case 'rejected':
        endpoint = `/api/admin/bda/${id}/reject/`;
        
        // Convert data to FormData for rejected case
        const rejectFormData = new FormData();
        
        // Map notes to comments as expected by the backend
        if (data.notes) {
          rejectFormData.append('comments', data.notes);
        }
        
        return apiClient.post(endpoint, rejectFormData);
        break;
      case 'revision_needed':
        endpoint = `/api/admin/bda/${id}/request_revision/`;
        
        // Convert data to FormData for revision_needed case
    const formData = new FormData();
    
        // Map the editable_fields to fields as expected by the backend
        if (data.editable_fields && Array.isArray(data.editable_fields)) {
          data.editable_fields.forEach(field => {
            formData.append('fields', field);
          });
        }
        
        // Map notes to comments as expected by the backend
        if (data.notes) {
          formData.append('comments', data.notes);
        }
        
        return apiClient.post(endpoint, formData);
        break;
      default:
        endpoint = `/api/admin/bda/${id}/update_status/`;
    }
    
    // For any other status, convert to FormData as well for consistency
    const defaultFormData = new FormData();
    for (const key in data) {
      if (key === 'notes') {
        defaultFormData.append('comments', data[key]);
      } else {
        defaultFormData.append(key, data[key]);
      }
    }
    
    return apiClient.post(endpoint, defaultFormData);
  },
  
  toggleActiveStatus: (id) => {
    console.log(`Toggling active status for BDA user ID: ${id}`);
    return apiClient.post(`/api/admin/users/${id}/toggle-status/`)
      .then(response => {
        console.log('Toggle status response:', response.data);
        return response.data;
      })
      .catch(error => {
        console.error('Error toggling BDA user status:', error);
        throw error;
      });
  },
  
  checkStatus: (mobileNumber, email) => {
    return apiClient.get('/api/admin/bda/check-status/', {
      params: { mobile_number: mobileNumber, email }
    });
  },
  
  getApplicationStatus: (token) => {
    return apiClient.get('/api/bda/status-check/', {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
  },
  
  getRevisionDetails: (token) => {
    return apiClient.get('/api/bda/revision-details/', {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
  },
  
  submitRevision: (formData, token) => {
    return apiClient.post('/api/bda/profile/submit_revision/', formData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  
  getApplicationHistory: (id) => {
    return apiClient.get(`/api/admin/bda-history/`, {
      params: { bda_id: id }
    });
  },

  requestRevision: (id, formData) => {
    // Ensure we're using the correct endpoint for requesting revisions
    return apiClient.post(`/api/admin/bda/${id}/request_revision/`, formData, {
      headers: {
        // Let the browser set the Content-Type with boundary for FormData
      }
    });
  },

  // Add BDA earnings API endpoints
  getEarnings: (page = 1, pageSize = 10, filters = {}) => {
    const params = {
      page,
      page_size: pageSize,
      ...filters
    };
    
    return apiClient.get('/api/bda/earnings/', { params })
      .then(response => {
        return paginationUtils.getPaginationInfo(response);
      })
      .catch(error => {
        console.error('Error fetching BDA earnings:', error);
        throw error;
      });
  },

  getEarningsSummary: () => {
    return apiClient.get('/api/bda/earnings-summary/')
      .then(response => response.data)
      .catch(error => {
        console.error('Error fetching BDA earnings summary:', error);
        throw error;
      });
  },

  getEarningsByMonth: (month, year) => {
    const params = { month, year };
    return apiClient.get('/api/bda/earnings/', { params })
      .then(response => response.data)
      .catch(error => {
        console.error('Error fetching BDA earnings for specific month:', error);
        throw error;
      });
  },

  // Password change endpoint
  changePassword: (data) => {
    return apiClient.patch('/api/bda/profile/change_password/', data)
      .then(response => response.data)
      .catch(error => {
        console.error('Error changing password:', error);
        throw error;
      });
  },

  // Terms acceptance endpoint
  acceptTerms: (data) => {
    return apiClient.patch('/api/bda/profile/accept_terms/', data)
      .then(response => response.data)
      .catch(error => {
        console.error('Error accepting terms:', error);
        throw error;
      });
  },

  // Add registration endpoint
  register: (formData) => {
    // For multipart/form-data with files
    if (formData instanceof FormData) {
      return apiClient.post('/api/bda/register/', formData, {
        headers: {
          'Content-Type': undefined // Let the browser set the correct boundary
        }
      });
    }
    return apiClient.post('/api/bda/register/', formData);
  },

  approve: (id, formData) => {
    return apiClient.post(`/api/admin/bda/${id}/approve/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },

  reject: (id, formData) => {
    return apiClient.post(`/api/admin/bda/${id}/reject/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
};
export const otpBdaSelfAPI = { 
  sendOTP: (data) => apiClient.post('/api/bda/send-otp/', data),
  verifyOTP: (data) => apiClient.post('/api/bda/verify-otp/', data),
}
export const otpSPSelfAPI = { 
  sendOTP: (data) => apiClient.post('/api/sp/send-otp/', data, {
    headers: {
      'Content-Type': undefined
    }
  }),
  verifyOTP: (data) => apiClient.post('/api/sp/verify-otp/', data, {
    headers: {
      'Content-Type': undefined
    }
  } ),
}

// BDA Commission API
export const bdaCommissionAPI = {
  getAll: () => {
    return apiClient.get('/api/admin/commissions/');
  },
  
  getOne: (id) => {
    return apiClient.get(`/api/admin/commissions/${id}/`);
  },
  
  create: (data) => {
    return apiClient.post('/api/admin/commissions/', data);
  },
  
  update: (id, data) => {
    return apiClient.put(`/api/admin/commissions/${id}/`, data);
  },
  
  delete: (id) => {
    return apiClient.delete(`/api/admin/commissions/${id}/`);
  },
  
  // BDA Earnings endpoints
  getBDAEarnings: (bdaId, filters = {}) => {
    const params = { bda: bdaId, ...filters };
    return apiClient.get('/api/admin/bda-earnings/', { params });
  },
  
  getBDAEarningsDetail: (id) => {
    return apiClient.get(`/api/admin/bda-earnings/${id}/`);
  },
  
  calculateBDAEarnings: (bdaId, month = null) => {
    const data = { bda_id: bdaId };
    if (month) {
      data.month = month;
    }
    return apiClient.post('/api/admin/bda-earnings/calculate/', data);
  },
  
  calculateAllBDAEarnings: (month = null) => {
    const data = {};
    if (month) {
      data.month = month;
    }
    return apiClient.post('/api/admin/bda-earnings/calculate-all/', data);
  },
  
  markEarningAsPaid: (earningId, notes = '') => {
    return apiClient.patch(`/api/admin/bda-earnings/${earningId}/mark-paid/`, {
      payment_notes: notes
    });
  },
  
  markEarningAsUnpaid: (earningId, notes = '') => {
    return apiClient.patch(`/api/admin/bda-earnings/${earningId}/mark-unpaid/`, {
      payment_notes: notes
    });
  },
  
  getEarningsSummary: (startDate = null, endDate = null) => {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    
    return apiClient.get('/api/admin/bda-earnings/summary/', { params });
  }
};

// BDA Commission Payment API
export const bdaCommissionPaymentAPI = {
  getAll: (page = 1, pageSize = 10, filters = {}) => 
    paginationUtils.getPaginatedData('/api/admin/bda-commission-payments/', page, pageSize, filters),
  getOne: (id) => apiClient.get(`/api/admin/bda-commission-payments/${id}/`),
  create: (data) => apiClient.post('/api/admin/bda-commission-payments/', data),
  update: (id, data) => apiClient.put(`/api/admin/bda-commission-payments/${id}/`, data),
  delete: (id) => apiClient.delete(`/api/admin/bda-commission-payments/${id}/`),
  getBdaPayments: (bdaId) => apiClient.get('/api/admin/bda-commission-payments/bda/', { params: { bda_id: bdaId } }),
  calculateCommission: (data) => apiClient.post('/api/admin/bda-commission-payments/calculate/', data),
  // Method for calculating volume-based commission for a month
  calculateMonthlyCommission: (bdaId, year, month) => apiClient.post('/api/admin/bda-commission-payments/calculate/', {
    bda_id: bdaId,
    source_type: 'easy_op_card',
    year: year,
    month: month
  }),
  // Method for calculating commission for a specific card
  calculateCardCommission: (bdaId, cardId) => apiClient.post('/api/admin/bda-commission-payments/calculate/', {
    bda_id: bdaId,
    source_type: 'easy_op_card',
    source_id: cardId
  }),
  // Method for calculating commission for a service provider
  calculateProviderCommission: (bdaId, providerId) => apiClient.post('/api/admin/bda-commission-payments/calculate/', {
    bda_id: bdaId,
    source_type: 'service_provider',
    source_id: providerId
  }),
};

// BDA Monthly Sales API
export const bdaMonthlySalesAPI = {
  getAll: (page = 1, pageSize = 10, filters = {}) => 
    paginationUtils.getPaginatedData('/api/admin/bda-monthly-sales/', page, pageSize, filters),
  getOne: (id) => apiClient.get(`/api/admin/bda-monthly-sales/${id}/`),
  create: (data) => apiClient.post('/api/admin/bda-monthly-sales/', data),
  update: (id, data) => apiClient.put(`/api/admin/bda-monthly-sales/${id}/`, data),
  delete: (id) => apiClient.delete(`/api/admin/bda-monthly-sales/${id}/`),
  syncMonthlyData: (data) => apiClient.post('/api/admin/bda-monthly-sales/sync/', data),
  getMonthlyCardSummary: (bdaId, year) => apiClient.get('/api/admin/bda-monthly-sales/summary/', { 
    params: { 
      bda_id: bdaId,
      year: year 
    } 
  }),
  getAggregateSummary: (year) => apiClient.get('/api/admin/bda-monthly-sales/summary/', { 
    params: { year } 
  }),
  // BDA-facing endpoint
  getBDAMonthlyStats: (year) => apiClient.get('/api/bda/monthly-sales/', { 
    params: { year } 
  }),
};

// BookMyOP Patient API
export const bookMyOPPatientAPI = {
  getAll: (page = 1, pageSize = 10, filters = {}) => 
    paginationUtils.getPaginatedData('/api/admin/patients/bookmyop/', page, pageSize, filters),
  getOne: (id) => apiClient.get(`/api/admin/patients/bookmyop/${id}/`),
  getAppointments: (id) => apiClient.get(`/api/admin/patients/bookmyop/${id}/appointments/`),
};

// EasyOP Patient API
export const easyOPPatientAPI = {
  getAll: (page = 1, pageSize = 10, filters = {}) => 
    paginationUtils.getPaginatedData('/api/admin/patients/easyop/', page, pageSize, filters),
  getOne: (id) => apiClient.get(`/api/admin/patients/easyop/${id}/`),
  getCards: (id) => apiClient.get(`/api/admin/patients/easyop/${id}/cards/`),
  getAppointments: (id) => apiClient.get(`/api/admin/patients/easyop/${id}/appointments/`),
  getFamilyMembers: (id) => apiClient.get(`/api/admin/patients/easyop/${id}/family_members/`),
};

// EasyOP Card Tariff API
// export const easyOPCardTariffAPI = {
//   getAll: (page = 1, pageSize = 10, filters = {}) => 
//     paginationUtils.getPaginatedData('/api/admin/card-tariffs/', page, pageSize, filters),
//   getOne: (id) => apiClient.get(`/api/admin/card-tariffs/${id}/`),
//   create: (data) => apiClient.post('/api/admin/card-tariffs/', data),
//   update: (id, data) => apiClient.put(`/api/admin/card-tariffs/${id}/`, data),
//   delete: (id) => apiClient.delete(`/api/admin/card-tariffs/${id}/`),
// };

// EasyOP Card API
export const easyOPCardAPI = {
  getAll: (page = 1, pageSize = 10, filters = {}) => 
    paginationUtils.getPaginatedData('/api/admin/cards/', page, pageSize, filters),
  getOne: (id) => apiClient.get(`/api/admin/cards/${id}/`),
  create: (data) => apiClient.post('/api/admin/cards/', data),
  update: (id, data) => apiClient.put(`/api/admin/cards/${id}/`, data),
  delete: (id) => apiClient.delete(`/api/admin/cards/${id}/`),
  getAppointments: (id) => apiClient.get(`/api/admin/cards/${id}/appointments/`),
};

// Appointment API
export const appointmentAPI = {
  getAll: async (params = {}) => {
    const { endpoint, ...restParams } = params;
    const baseUrl = endpoint 
      ? `/api/admin/appointments/${endpoint}/`
      : '/api/admin/appointments/';
    
    const response = await apiClient.get(baseUrl, { params: restParams });
    return response.data;
  },
  getOne: (id) => apiClient.get(`/api/admin/appointments/${id}/`),
  create: (data) => apiClient.post('/api/admin/appointments/', data),
  update: (id, data) => apiClient.put(`/api/admin/appointments/${id}/`, data),
  delete: (id) => apiClient.delete(`/api/admin/appointments/${id}/`),
  updateStatus: (id, data) => apiClient.post(`/api/admin/appointments/${id}/update-status/`, data),
  getToday: () => apiClient.get('/api/admin/appointments/today/'),
  getUpcoming: () => apiClient.get('/api/admin/appointments/upcoming/'),
  getPast: () => apiClient.get('/api/admin/appointments/past/'),
  getStatistics: () => apiClient.get('/api/admin/appointments/statistics/'),
};


// Promo Code API
export const promoCodeAPI = {
  getAll: (page = 1, pageSize = 10, filters = {}) => 
    paginationUtils.getPaginatedData('/api/admin/promo-codes/', page, pageSize, filters),
  getOne: (id) => apiClient.get(`/api/admin/promo-codes/${id}/`),
  create: (data) => apiClient.post('/api/admin/promo-codes/', data),
  update: (id, data) => apiClient.put(`/api/admin/promo-codes/${id}/`, data),
  delete: (id) => apiClient.delete(`/api/admin/promo-codes/${id}/`),
  getUsages: (id) => apiClient.get(`/api/admin/promo-codes/${id}/usages/`),
};

// Communication API
export const communicationAPI = {
  // Get all tickets with filters
  getAll: async (params = {}) => {
    try {
      const response = await apiClient.get('/api/admin/communications/', { params });
      return response;
    } catch (error) {
      throw error;
    }
  },
  getCounts: async (params = {}) => {
    try {
      const response = await apiClient.get('/api/admin/communications/get_counts/');
      return response;
    } catch (error) {
      throw error;
    }
  },

  // Get single ticket
  get: async (id) => {
    try {
      const response = await apiClient.get(`/api/admin/communications/${id}/`);
      return response;
    } catch (error) {
      throw error;
    }
  },

  // Create new ticket
  create: async (formData) => {
    try {
      const response = await apiClient.post('/api/admin/communications/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  // Reply to ticket
  reply: async (ticketId, formData) => {
    try {
      const response = await apiClient.post(`/api/admin/communications/${ticketId}/reply/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  // Close ticket
  adminCloseTicket: async (ticketId) => {
    try {
      const response = await apiClient.post(`/api/admin/communications/${ticketId}/close_ticket/`);
      return response;
    } catch (error) {
      throw error;
    }
  },

  // Reopen ticket
  adminReopenTicket: async (ticketId) => {
    try {
      const response = await apiClient.post(`/api/admin/communications/${ticketId}/reopen_ticket/`);
      return response;
    } catch (error) {
      throw error;
    }
  },

  // Mark ticket as resolved
  adminMarkResolved: async (ticketId) => {
    try {
      const response = await apiClient.post(`/api/admin/communications/${ticketId}/mark_resolved/`);
      return response;
    } catch (error) {
      throw error;
    }
  }
};

// Acceptance Letter API
export const acceptanceLetterAPI = {
  getAll: (page = 1, pageSize = 10, filters = {}) => 
    paginationUtils.getPaginatedData('/api/admin/acceptance-letters/', page, pageSize, filters),
  getOne: (id) => apiClient.get(`/api/admin/acceptance-letters/${id}/`),
};

// System Settings API
export const systemSettingsAPI = {
  getSettings: () => apiClient.get('/api/admin/settings/'),
  updateSettings: (data) => apiClient.put('/api/admin/settings/', data),
};

// Admin User Management API
export const adminUserAPI = {
  getAll: (page = 1, pageSize = 10, filters = {}) => 
    paginationUtils.getPaginatedData('/api/admin/users/', page, pageSize, filters),
  getOne: (id) => apiClient.get(`/api/admin/users/${id}/`),
  create: (data) => {
    return apiClient.post('/api/admin/users/', data)
      .then(response => response)
      .catch(error => { throw error; });
  },
  update: (id, data) => {
    return apiClient.patch(`/api/admin/users/${id}/`, data)
      .then(response => response)
      .catch(error => { throw error; });
  },
  delete: (id) => apiClient.delete(`/api/admin/users/${id}/`),
  updatePermissions: (id, data) => apiClient.patch(`/api/admin/users/${id}/permissions/`, data),
  getPermissions: () => apiClient.get('/api/admin/permissions/'),
  getProfile: () => apiClient.get('/api/admin/auth/me/'),
  toggleActiveStatus: (id) => {
    return apiClient.post(`/api/admin/users/${id}/toggle-status/`)
      .then(response => response.data)
      .catch(error => { throw error; });
  },
};

// Admin Staff Management Service
const staffManagementAPI = {
  /**
   * Create a new admin staff member
   * @param {Object} data - Admin staff creation data
   * @returns {Promise} Promise resolving to created admin staff
   */
  createAdminStaff: async (data) => {
    try {
      // Validate required fields
      const requiredFields = ['firstName', 'lastName', 'email', 'password', 'role'];
      requiredFields.forEach(field => {
        if (!data[field]) {
          throw new Error(`${field} is required`);
        }
      });

      // Ensure passwords match (if applicable)
      if (data.password !== data.confirmPassword) {
        throw new Error('Passwords do not match');
      }

      // Prepare payload
      const payload = {
        first_name: data.firstName,
        last_name: data.lastName,
        email: data.email,
        password: data.password,
        confirm_password: data.confirmPassword,
        phone: data.phone || null,
        role: data.role || 'admin_staff',
        managed_regions: data.managedRegions || null
      };

      // Make API call
      const response = await apiClient.post('/admin-staff/create/', payload);

      return {
        success: true,
        data: response.data,
        message: 'Staff member created successfully'
      };
    } catch (error) {
      console.error('Error creating admin staff:', error);
      
      // More detailed error handling
      const errorMessage = error.response?.data?.detail || 
                           error.response?.data?.message || 
                           'Failed to create staff member';
      
      return {
        success: false,
        message: errorMessage
      };
    }
  },

  /**
   * Fetch list of admin staff members
   * @param {number} page - Page number
   * @param {number} pageSize - Number of items per page
   * @param {Object} filters - Optional filters
   * @returns {Promise} Promise resolving to paginated admin staff list
   */
  getAdminStaff: async (page = 1, pageSize = 10, filters = {}) => {
    try {
      const response = await apiClient.get('/admin-staff/', {
        params: {
          page,
          page_size: pageSize,
          ...filters
        }
      });

      // Handle different possible response structures
      if (response.data && response.data.results) {
        // Django REST Framework style pagination
        return {
          data: response.data.results,
          totalPages: Math.ceil(response.data.count / pageSize),
          currentPage: page,
          totalCount: response.data.count
        };
      } else if (Array.isArray(response.data)) {
        // Simple array response
        return {
          data: response.data,
          totalPages: 1,
          currentPage: 1,
          totalCount: response.data.length
        };
      } else {
        throw new Error('Unexpected response format');
      }
    } catch (error) {
      console.error('Error fetching admin staff:', error);
      throw error;
    }
  },

  /**
   * Get details of a specific admin staff member
   * @param {number} id - Admin staff ID
   * @returns {Promise} Promise resolving to admin staff details
   */
  getAdminStaffDetails: async (id) => {
    try {
      const response = await apiClient.get(`/admin-staff/${id}/`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error(`Error fetching admin staff details for ID ${id}:`, error);
      return {
        success: false,
        message: error.response?.data?.detail || 'Failed to fetch staff details'
      };
    }
  },

  /**
   * Update an admin staff member's details
   * @param {number} id - Admin staff ID
   * @param {Object} data - Updated staff data
   * @returns {Promise} Promise resolving to updated staff details
   */
  updateAdminStaff: async (id, data) => {
    try {
      const payload = {
        first_name: data.firstName,
        last_name: data.lastName,
        phone: data.phone || null,
        role: data.role || 'admin_staff',
        managed_regions: data.managedRegions || null
      };

      const response = await apiClient.patch(`/admin-staff/${id}/`, payload);

      return {
        success: true,
        data: response.data,
        message: 'Staff member updated successfully'
      };
    } catch (error) {
      console.error(`Error updating admin staff with ID ${id}:`, error);
      
      return {
        success: false,
        message: error.response?.data?.detail || 'Failed to update staff member'
      };
    }
  },

  /**
   * Delete an admin staff member
   * @param {number} id - Admin staff ID
   * @returns {Promise} Promise resolving to deletion result
   */
  deleteAdminStaff: async (id) => {
    try {
      await apiClient.delete(`/admin-staff/${id}/`);
      return {
        success: true,
        message: 'Staff member deleted successfully'
      };
    } catch (error) {
      console.error(`Error deleting admin staff with ID ${id}:`, error);
      
      return {
        success: false,
        message: error.response?.data?.detail || 'Failed to delete staff member'
      };
    }
  },

  /**
   * Fetch permissions for admin staff
   * @returns {Promise} Promise resolving to available permissions
   */
  getPermissions: async () => {
    try {
      const response = await apiClient.get('/admin-staff/permissions/');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error fetching permissions:', error);
      return {
        success: false,
        message: error.response?.data?.detail || 'Failed to fetch permissions'
      };
    }
  },

  /**
   * Update staff member permissions
   * @param {number} id - Admin staff ID
   * @param {Array} permissions - List of permission IDs
   * @returns {Promise} Promise resolving to update result
   */
  updateStaffPermissions: async (id, permissions) => {
    try {
      const response = await apiClient.patch(`/admin-staff/${id}/permissions/`, { 
        permissions 
      });

      return {
        success: true,
        data: response.data,
        message: 'Permissions updated successfully'
      };
    } catch (error) {
      console.error(`Error updating permissions for staff ID ${id}:`, error);
      
      return {
        success: false,
        message: error.response?.data?.detail || 'Failed to update permissions'
      };
    }
  }
};

// Service Provider Portal API endpoints
export const serviceProviderPortalAPI = {
  // Auth endpoints
  csrf: () => apiClient.get('/api/sp/auth/csrf/'),
  login: (credentials) => apiClient.post('/api/sp/auth/login/', credentials),
  refreshToken: (refreshToken) => apiClient.post('/api/sp/auth/refresh/', { refresh_token: refreshToken }),
  me: () => apiClient.get('/api/sp/auth/me/'),
  changePassword: (data) => apiClient.post('/api/sp/auth/change-password/', data),
  resetTempPassword: (data) => apiClient.post('/api/sp/reset-temp-password/', data),
  register: (data) => apiClient.post('/api/sp/register/', data),

  // Profile management
  getProfile: () => apiClient.get('/api/sp/profile/'),
  updateProfile: (data) => {
    // For multipart/form-data with files
    if (data instanceof FormData) {
      return apiClient.put('/api/sp/profile/', data, {
        headers: { 'Content-Type': undefined }
      });
    }
    return apiClient.put('/api/sp/profile/', data);
  },

  // Doctor/time-slot & availability
  getDoctorTimeSlots: (doctorId) => apiClient.get(`/api/sp/doctors/${doctorId}/time-slots/`),
  /**
   * Per-date aggregated availability across platforms
   * GET /api/sp/doctors/{doctor_id}/availability?date=YYYY-MM-DD
   */
  getDoctorAvailability: (doctorId, date) =>
    apiClient.get(`/api/sp/doctors/${doctorId}/availability`, { params: { date } }),
  /**
   * Per-slot availability view
   * GET /api/sp/doctors/{doctor_id}/slots/{slot_id}/appointments/
   */
  getDoctorSlotAvailability: (doctorId, slotId) =>
    apiClient.get(`/api/sp/doctors/${doctorId}/slots/${slotId}/appointments/`),

  getDashboard: () => apiClient.get('/api/sp/profile/dashboard/'),

  // Patient management
  getPatients: (params = {}) => apiClient.get('/api/sp/patients/', { params }),
  getPatientDetails: (patientId) => apiClient.get(`/api/sp/patients/${patientId}/`),
  getPatientAppointments: (patientId) => apiClient.get(`/api/sp/patients/${patientId}/appointments/`),
  getPatientDocuments: (patientId) => apiClient.get(`/api/sp/patients/${patientId}/documents/`),
  getPatientStatistics: () => apiClient.get('/api/sp/patients/statistics/'),

  // Specialty management
  getSpecialties: (page = 1, pageSize = 10, filters = {}) => 
    paginationUtils.getPaginatedData('/api/sp/specialties/', page, pageSize, filters),
  getProviderSpecialties: (page = 1, pageSize = 10, filters = {}) => 
    paginationUtils.getPaginatedData('/api/sp/provider-specialties/', page, pageSize, filters),
  createProviderSpecialty: (data) => apiClient.post('/api/sp/provider-specialties/', data),
  updateProviderSpecialty: (id, data) => apiClient.put(`/api/sp/provider-specialties/${id}/`, data),
  deleteProviderSpecialty: (id) => apiClient.delete(`/api/sp/provider-specialties/${id}/`),

  // Symptom management
  getSymptoms: (page = 1, pageSize = 10, filters = {}) => 
    paginationUtils.getPaginatedData('/api/sp/symptoms/', page, pageSize, filters),
  searchSymptoms: (query) => apiClient.get('/api/sp/symptoms/', { 
    params: { search: query } 
  }),

  // Doctor management
  getDoctors: (page = 1, pageSize = 10, filters = {}) => {
    const params = { 
      page, 
      page_size: pageSize,
      ...filters 
    };
    
    // Add symptom filtering support
    if (filters.symptoms) {
      params.symptoms = Array.isArray(filters.symptoms) ? filters.symptoms.join(',') : filters.symptoms;
    }
    
    return apiClient.get('/api/sp/doctors/', { params });
  },
  getDoctor: (id) => apiClient.get(`/api/sp/doctors/${id}/`),
  createDoctor: (data) => {
    if (data instanceof FormData) {
      return apiClient.post('/api/sp/doctors/', data);
    }
    return apiClient.post('/api/sp/doctors/', data);
  },
  updateDoctor: (id, data) => {
    if (data instanceof FormData) {
      return apiClient.patch(`/api/sp/doctors/${id}/`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    }
    return apiClient.patch(`/api/sp/doctors/${id}/`, data);
  },
  deleteDoctor: (id) => apiClient.delete(`/api/sp/doctors/${id}/`),
  getDoctorTimeSlots: (doctorId) => apiClient.get(`/api/sp/doctors/${doctorId}/time-slots/`),
  getDoctorSchedules: (id) => apiClient.get(`/api/sp/doctors/${id}/schedules/`),
  getDoctorAppointments: (id) => apiClient.get(`/api/sp/doctors/${id}/appointments/`),
  
  // Doctor symptom management
  updateDoctorSymptoms: (id, symptomIds) => apiClient.patch(`/api/sp/doctors/${id}/`, {
    area_of_expertise: symptomIds
  }),
  getDoctorSymptoms: (id) => apiClient.get(`/api/sp/doctors/${id}/`).then(response => 
    response.data.area_of_expertise || []
  ),
  addDoctorSymptoms: (id, symptomIds) => apiClient.patch(`/api/sp/doctors/${id}/`, {
    area_of_expertise: symptomIds
  }),
  removeDoctorSymptoms: (id, symptomIds) => apiClient.patch(`/api/sp/doctors/${id}/`, {
    area_of_expertise: symptomIds
  }),

  // Time slot management
  getTimeSlots: (page = 1, pageSize = 10, filters = {}) => 
    paginationUtils.getPaginatedData('/api/sp/time-slots/', page, pageSize, filters),
  createTimeSlot: (data) => apiClient.post('/api/sp/time-slots/', data),
  updateTimeSlot: (id, data) => apiClient.put(`/api/sp/time-slots/${id}/`, data),
  deleteTimeSlot: (id) => apiClient.delete(`/api/sp/time-slots/${id}/`),

  // New bulk time slot methods
  bulkCreateTimeSlots: async (doctorId, slots) => {
    try {
      const response = await apiClient.post('/api/sp/time-slots/bulk_create_doctor_slots/', {
        doctor_id: doctorId,
        slots: slots
      });
      return response.data;
    } catch (error) {
      console.error('Error in bulk creating time slots:', error);
      throw error;
    }
  },

  bulkUpdateDoctorSlots: async (doctorId, slots, slotType) => {
    try {
      const response = await apiClient.post('/api/sp/time-slots/bulk_update_doctor_slots/', {
        doctor_id: doctorId,
        slots: slots,
        slot_type: slotType
      });
      return response.data;
    } catch (error) {
      console.error('Error in bulk updating doctor slots:', error);
      throw error;
    }
  },

  bulkUpdateMultipleDoctorsSlots: async (doctorsSlots) => {
    try {
      const response = await apiClient.post('/api/sp/time-slots/bulk_update_multiple_doctors/', {
        doctors_slots: doctorsSlots
      });
      return response.data;
    } catch (error) {
      console.error('Error in bulk updating multiple doctors slots:', error);
      throw error;
    }
  },

  // Appointment management
  getTodayAppointments: () => apiClient.get('/api/sp/appointments/today/'),
  getUpcomingAppointments: () => apiClient.get('/api/sp/appointments/upcoming/'),
  confirmAppointment: (id) => apiClient.post(`/api/sp/appointments/${id}/confirm/`),
  completeAppointment: (id) => apiClient.post(`/api/sp/appointments/${id}/complete/`),
  cancelAppointment: (id, cancellationData) => apiClient.post(`/api/sp/appointments/${id}/cancel/`, cancellationData),
  markNoShow: (id) => apiClient.post(`/api/sp/appointments/${id}/no_show/`),

  // Staff management
  getStaff: (page = 1, pageSize = 10, filters = {}) => 
    paginationUtils.getPaginatedData('/api/sp/staff/', page, pageSize, filters),
  getAllStaff: () => apiClient.get('/api/sp/staff/all_staff/'),
  createStaff: (data) => apiClient.post('/api/sp/staff/', data),
  updateStaff: (id, data) => apiClient.put(`/api/sp/staff/${id}/`, data),
  deleteStaff: (id) => apiClient.delete(`/api/sp/staff/${id}/`),
  resetStaffPassword: (id, data) => apiClient.post(`/api/sp/staff/${id}/reset_password/`, data),
  activateStaff: (id) => apiClient.post(`/api/sp/staff/${id}/activate/`),
  deactivateStaff: (id) => apiClient.post(`/api/sp/staff/${id}/deactivate/`),

  // Support tickets
  getTickets: (page = 1, pageSize = 10, filters = {}) => 
    paginationUtils.getPaginatedData('/api/sp/tickets/', page, pageSize, filters),
  getTicket: (id) => apiClient.get(`/api/sp/tickets/${id}/`),
  createTicket: (data) => {
    if (data instanceof FormData) {
      return apiClient.post('/api/sp/tickets/', data, {
        headers: { 'Content-Type': undefined }
      });
    }
    return apiClient.post('/api/sp/tickets/', data);
  },
  replyToTicket: (id, data) => {
    if (data instanceof FormData) {
      return apiClient.post(`/api/sp/tickets/${id}/reply/`, data, {
        headers: { 'Content-Type': undefined }
      });
    }
    return apiClient.post(`/api/sp/tickets/${id}/reply/`, data);
  },
  closeTicket: (id) => apiClient.post(`/api/sp/tickets/${id}/close_ticket/`),
  reopenTicket: (id) => apiClient.post(`/api/sp/tickets/${id}/reopen_ticket/`),

  // Notifications
  getNotifications: (page = 1, pageSize = 10, filters = {}) => 
    paginationUtils.getPaginatedData('/api/sp/notifications/', page, pageSize, filters),
  getUnreadNotifications: () => apiClient.get('/api/sp/notifications/unread/'),
  getUnreadCount: () => apiClient.get('/api/sp/notifications/count_unread/'),
  markNotificationRead: (id) => apiClient.post(`/api/sp/notifications/${id}/mark_read/`),
  markAllNotificationsRead: () => apiClient.post('/api/sp/notifications/mark_all_read/'),

  // Platform settings
  getBookMyOpSettings: (id) => apiClient.get(`/api/sp/service-providers/${id}/book_my_op_settings/`),
  updateBookMyOpSettings: (id, data) => apiClient.put(`/api/sp/service-providers/${id}/book_my_op_settings/`, data),
  getEasyOpSettings: (id) => apiClient.get(`/api/sp/service-providers/${id}/easy_op_settings/`),
  updateEasyOpSettings: (id, data) => apiClient.put(`/api/sp/service-providers/${id}/easy_op_settings/`, data),

  getAppointments: async (params = {}) => {
    try {
      // Default pagination if not provided
      const defaultParams = {
        page: 1,
        page_size: 20,
        ...params
      };
      const response = await apiClient.get('/api/sp/appointments/', { params: defaultParams });
      return response;
    } catch (error) {
      console.error('Error fetching appointments:', error);
      throw error;
    }
  },
  getAppointmentDetails: async (appointmentId) => {
    try {
      const response = await apiClient.get(`/api/sp/appointments/${appointmentId}/`);
      return response;
    } catch (error) {
      console.error('Error fetching appointment details:', error);
      throw error;  
    }
  },

  confirmAppointment: async (appointmentId) => {
    try {
      const response = await apiClient.post(`/api/sp/appointments/${appointmentId}/confirm/`);
      return response;
    } catch (error) {
      console.error('Error confirming appointment:', error);
      throw error;
    }
  },

  completeAppointment: async (appointmentId) => {
    try {
      const response = await apiClient.post(`/api/sp/appointments/${appointmentId}/complete/`);
      return response;
    } catch (error) {
      console.error('Error completing appointment:', error);
      throw error;
    }
  },

  cancelAppointment: async (appointmentId) => {
    try {
      const response = await apiClient.post(`/api/sp/appointments/${appointmentId}/cancel/`);
      return response;
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      throw error;
    }
  },
  markNoShow: async (appointmentId) => {
    try {
      const response = await apiClient.post(`/api/sp/appointments/${appointmentId}/no_show/`);
      return response;
    } catch (error) {
      console.error('Error marking appointment as no-show:', error);
      throw error;
    }
  },

  // Block appointments
  blockAppointments: async (data) => {
    try {
      const response = await apiClient.post('/api/sp/appointments/block/', data);
      return response;
    } catch (error) {
      console.error('Error blocking appointments:', error);
      throw error;
    }
  },

  // Delay appointments
  delayAppointments: async (data) => {
    try {
      const response = await apiClient.post('/api/sp/appointments/delay_appointments/', data);
      return response.data;
    } catch (error) {
      console.error('Error delaying appointments:', error);
      throw error;
    }
  },
// Add these inside the serviceProviderPortalAPI object

// Advertisement management
getAdvertisements: (page = 1, pageSize = 10, filters = {}) => 
  paginationUtils.getPaginatedData('/api/sp/advertisements/', page, pageSize, filters),

getAdvertisement: (id) => 
  apiClient.get(`/api/sp/advertisements/${id}/`),

createAdvertisement: (data) => {
  // Handle FormData for image upload
  if (data instanceof FormData) {
    return apiClient.post('/api/sp/advertisements/', data, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
  return apiClient.post('/api/sp/advertisements/', data);
},

updateAdvertisement: (id, data) => {
  // Handle FormData for image upload
  if (data instanceof FormData) {
    return apiClient.patch(`/api/sp/advertisements/${id}/`, data, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
  return apiClient.patch(`/api/sp/advertisements/${id}/`, data);
},

deleteAdvertisement: (id) => 
  apiClient.delete(`/api/sp/advertisements/${id}/`),

getAdvertisementCostEstimate: (id) => 
  apiClient.get(`/api/sp/advertisements/${id}/cost_estimate/`),

makeAdvertisementPayment: (id) => 
  apiClient.post(`/api/sp/advertisements/${id}/make_payment/`),

calculateAdvertisementEstimate: (data) => {
  // Handle FormData for calculation with form data
  if (data instanceof FormData) {
    return apiClient.post('/api/sp/advertisements/calculate_estimate/', data, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
  return apiClient.post('/api/sp/advertisements/calculate_estimate/', data);
},

cancelAdvertisementRequest: (id) => 
  apiClient.post(`/api/sp/advertisements/${id}/cancel_request/`),
  getServiceProviderAppointmentCalendar: async ({ start_date, end_date, doctor_id, specialty_id,slot_type }) => {
    try {
      const params = {
        start_date: start_date,
        end_date: end_date,
        doctor_id: doctor_id,
        specialty_id: specialty_id,
        slot_type: slot_type
      };
      
      const response = await apiClient.get('/api/sp/appointments/calendar/', { params });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error fetching calendar data:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message
      };
    }
  },
  
  // Memberships
  /**
   * List memberships for the authenticated service provider user
   * Query supports ordering, pagination, and filters per docs
   */
  getMemberships: (page = 1, pageSize = 10, filters = {}) =>
    paginationUtils.getPaginatedData('/api/sp/memberships/', page, pageSize, filters),
  
  /** Retrieve one membership by id */
  getMembership: (id) => apiClient.get(`/api/sp/memberships/${id}/`),
  
  /** Create a membership */
  createMembership: (data) => apiClient.post('/api/sp/memberships/', data),
  
  /** Partially update a membership */
  updateMembership: (id, data) => apiClient.patch(`/api/sp/memberships/${id}/`, data),
  
  /** Delete a membership */
  deleteMembership: (id) => apiClient.delete(`/api/sp/memberships/${id}/`),
  
};

// ======================================================
// Notification API methods
// ======================================================

export const notificationApi = {
  // Get all notifications
  getNotifications: async (params = {}) => {
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const userRole = localStorage.getItem('userRole') || '';
      const role = userData.role || userRole || 'admin';
      const userType = userData.user_type || '';
      
      let endpoint = '/api/admin/notifications/';
      
      if (role === 'bda') {
        endpoint = '/api/bda/notifications/';
      } else if (role === 'service-provider' || role === 'sp_staff' || 
                userType === 'sp_admin' || userType === 'sp_staff') {
        endpoint = '/api/sp/notifications/';
      }
      
      const response = await apiClient.get(endpoint, { params });
      return Array.isArray(response.data) ? response.data : 
             response.data?.results || response.data?.items || [];
    } catch (error) {
      console.error('Error fetching notifications:', error);
      return [];
    }
  },

  // Get unread notifications count
  getUnreadCount: async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const userRole = localStorage.getItem('userRole') || '';
      const role = userData.role || userRole || 'admin';
      const userType = userData.user_type || '';
      
      let endpoint = '/api/admin/notifications/count_unread/';
      
      if (role === 'bda') {
        endpoint = '/api/bda/notifications/count_unread/';
      } else if (role === 'service-provider' || role === 'sp_staff' || 
                userType === 'sp_admin' || userType === 'sp_staff') {
        endpoint = '/api/sp/notifications/count_unread/';
      }

      // Get the auth token
      const token = localStorage.getItem('auth_token');
      if (!token) {
        console.warn('No auth token found for notification count request');
        return 0;
      }

      // Set up request config with explicit auth header
      const config = {
        headers: {
          'Authorization': token.startsWith('Bearer ') || token.startsWith('Token ') 
            ? token 
            : `Bearer ${token}`
        }
      };
      
      console.log(`getUnreadCount: calling ${endpoint} with token: ${config.headers.Authorization.substring(0, 20)}...`);
      const response = await apiClient.get(endpoint, config);
      return response.data?.unread_count || 0;
    } catch (error) {
      console.error('Error fetching unread count:', error);
      return 0;
    }
  },

  // Mark a notification as read
  markAsRead: async (id) => {
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const role = userData.role || 'admin';
      
      let endpoint = `/api/admin/notifications/${id}/mark_read/`;
      if (role === 'bda') {
        endpoint = `/api/bda/notifications/${id}/mark_read/`;
      } else if (role === 'service-provider') {
        endpoint = `/api/sp/notifications/${id}/mark_read/`;
      }
      
      const response = await apiClient.post(endpoint);
      return response.data;
    } catch (error) {
      console.error(`Error marking notification #${id} as read:`, error);
      throw error;
    }
  },

  // Mark all notifications as read
  markAllAsRead: async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const role = userData.role || 'admin';
      
      let endpoint = '/api/admin/notifications/mark_all_read/';
      if (role === 'bda') {
        endpoint = '/api/bda/notifications/mark_all_read/';
      } else if (role === 'service-provider') {
        endpoint = '/api/sp/notifications/mark_all_read/';
      }
      
      const response = await apiClient.post(endpoint);
      return response.data;
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  },

  // Clear expired notifications
  clearExpired: async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const role = userData.role || 'admin';
      
      let endpoint = '/api/admin/notifications/clear-expired/';
      if (role === 'bda') {
        endpoint = '/api/bda/notifications/clear-expired/';
      } else if (role === 'service-provider') {
        endpoint = '/api/sp/notifications/clear-expired/';
      }
      
      const response = await apiClient.post(endpoint);
      return response.data;
    } catch (error) {
      console.error('Error clearing expired notifications:', error);
      throw error;
    }
  }
};

// ======================================================
// Ticketing System API
// ======================================================

export const ticketingAPI = {
  // Admin methods
  adminGetTickets: (page = 1, pageSize = 10, filters = {}) => 
    paginationUtils.getPaginatedData('/api/admin/communications/', page, pageSize, filters),
  adminGetMyTickets: () => apiClient.get('/api/admin/communications/my/'),
  adminGetTicket: (id) => apiClient.get(`/api/admin/communications/${id}/`),
  adminReplyToTicket: (id, formData) => {
    // Check if formData is already a FormData object
    if (formData instanceof FormData) {
      return apiClient.post(`/api/admin/communications/${id}/reply/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    }
    
    // Legacy handling for separate parameters (for backward compatibility)
    const { message, attachment = null, closeTicket = false } = formData || {};
    
    if (attachment) {
      const data = new FormData();
      data.append('message', message);
      data.append('is_from_admin', true);
      data.append('attachment', attachment);
      data.append('close_ticket', closeTicket);
      
      return apiClient.post(`/api/admin/communications/${id}/reply/`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    }
    
    return apiClient.post(`/api/admin/communications/${id}/reply/`, { 
      message, 
      is_from_admin: true,
      close_ticket: closeTicket
    });
  },
  adminCloseTicket: (id) => apiClient.patch(`/api/admin/communications/${id}/close/`),
  adminReopenTicket: (id) => apiClient.patch(`/api/admin/communications/${id}/reopen/`),
  
  // BDA methods
  bdaGetTickets: (page = 1, pageSize = 10, filters = {}) => 
    paginationUtils.getPaginatedData('/api/bda/tickets/', page, pageSize, filters),
  bdaGetMyTickets: () => apiClient.get('/api/bda/tickets/my/'),
  bdaGetTicket: (id) => apiClient.get(`/api/bda/tickets/${id}/`),
  bdaCreateTicket: (message, subject = "Support Request", attachment = null) => {
    // Always use FormData to ensure proper content type
    const formData = new FormData();
    formData.append('message', message);
    formData.append('subject', subject);
    formData.append('is_from_admin', false);
    
    if (attachment) {
      formData.append('attachment', attachment);
    }
    
    return apiClient.post('/api/bda/tickets/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  bdaReplyToTicket: (id, formData, attachment = null, closeTicket = false) => {
    // Check if formData is already a FormData object
    if (formData instanceof FormData) {
      return apiClient.post(`/api/bda/tickets/${id}/reply/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    }
    
    // Create a new FormData object for all cases
    const data = new FormData();
    
    // formData parameter could be the message text or an object
    if (typeof formData === 'string') {
      data.append('message', formData);
    } else if (formData && typeof formData === 'object') {
      // Handle case where formData is an object with message property
      data.append('message', formData.message || '');
      
      // Use attachment from the object if available
      if (formData.attachment) {
        attachment = formData.attachment;
      }
      
      // Use closeTicket from the object if available
      if (formData.closeTicket !== undefined) {
        closeTicket = formData.closeTicket;
      }
    }
    
    data.append('is_from_admin', false);
    data.append('close_ticket', closeTicket);
    
    if (attachment) {
      data.append('attachment', attachment);
    }
    
    return apiClient.post(`/api/bda/tickets/${id}/reply/`, data, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  bdaCloseTicket: (id) => apiClient.patch(`/api/bda/tickets/${id}/close/`),
  bdaReopenTicket: (id) => apiClient.patch(`/api/bda/tickets/${id}/reopen/`),
  
  // Service Provider methods
  providerGetTickets: (page = 1, pageSize = 10, filters = {}) => 
    paginationUtils.getPaginatedData('/api/sp/tickets/', page, pageSize, filters),
  providerGetMyTickets: () => apiClient.get('/api/sp/tickets/my/'),
  providerGetTicket: async (id) => {
    try {
      // Get the auth token
      const token = localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await apiClient.get(`/api/sp/tickets/${id}/`, {
        headers: {
          'Authorization': token.startsWith('Bearer ') ? token : `Bearer ${token}`,
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      return response;
    } catch (error) {
      console.error(`Error getting ticket ${id}:`, error);
      throw error;
    }
  },
  providerCreateTicket: async (formData) => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await apiClient.post('/api/sp/tickets/', formData, {
        headers: {
          'Authorization': token.startsWith('Bearer ') ? token : `Bearer ${token}`,
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'Content-Type': 'multipart/form-data'
        }
      });
      return response;
    } catch (error) {
      console.error('Error creating ticket:', error);
      throw error;
    }
  },
  providerReplyToTicket: async (id, formData) => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await apiClient.post(`/api/sp/tickets/${id}/reply/`, formData, {
        headers: {
          'Authorization': token.startsWith('Bearer ') ? token : `Bearer ${token}`,
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'Content-Type': 'multipart/form-data'
        }
      });
      return response;
    } catch (error) {
      console.error(`Error replying to ticket ${id}:`, error);
      throw error;
    }
  },
  providerCloseTicket: async (id) => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await apiClient.post(`/api/sp/tickets/${id}/close_ticket/`, {}, {
        headers: {
          'Authorization': token.startsWith('Bearer ') ? token : `Bearer ${token}`,
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      return response;
    } catch (error) {
      console.error(`Error closing ticket ${id}:`, error);
      throw error;
    }
  },
  providerReopenTicket: async (id) => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await apiClient.post(`/api/sp/tickets/${id}/reopen_ticket/`, {}, {
        headers: {
          'Authorization': token.startsWith('Bearer ') ? token : `Bearer ${token}`,
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      return response;
    } catch (error) {
      console.error(`Error reopening ticket ${id}:`, error);
      throw error;
    }
  },
  
  // Common methods - detect user role from stored data and route to appropriate endpoint
  getTickets: (page = 1, pageSize = 10, filters = {}) => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const role = userData.role || 'admin'; // Default to admin if no role found
    
    switch(role) {
      case 'bda':
        return ticketingAPI.bdaGetTickets(page, pageSize, filters);
      case 'provider':
        return ticketingAPI.providerGetTickets(page, pageSize, filters);
      case 'admin':
      default:
        return ticketingAPI.adminGetTickets(page, pageSize, filters);
    }
  },
  
  getMyTickets: () => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const role = userData.role || 'admin'; // Default to admin if no role found
    
    switch(role) {
      case 'bda':
        return ticketingAPI.bdaGetMyTickets();
      case 'provider':
        return ticketingAPI.providerGetMyTickets();
      case 'admin':
      default:
        return ticketingAPI.adminGetMyTickets();
    }
  },
  
  getTicket: (id) => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const role = userData.role || 'admin'; // Default to admin if no role found
    
    switch(role) {
      case 'bda':
        return ticketingAPI.bdaGetTicket(id);
      case 'provider':
        return ticketingAPI.providerGetTicket(id);
      case 'admin':
      default:
        return ticketingAPI.adminGetTicket(id);
    }
  },
  
  createTicket: (message, subject = "Support Request", attachment = null) => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const role = userData.role || 'admin'; // Default to admin if no role found
    
    switch(role) {
      case 'bda':
        return ticketingAPI.bdaCreateTicket(message, subject, attachment);
      case 'provider':
        return ticketingAPI.providerCreateTicket({ message, subject, attachment });
      case 'admin':
      default:
        // Admins typically don't create tickets, they respond to them
        console.warn('Admins cannot create tickets, only respond to them');
        return Promise.reject(new Error('Admins cannot create tickets, only respond to them'));
    }
  },
  
  replyToTicket: (id, messageOrFormData, attachment = null, closeTicket = false) => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const role = userData.role || 'admin'; // Default to admin if no role found
    
    // If messageOrFormData is a FormData object, pass it directly
    if (messageOrFormData instanceof FormData) {
      switch(role) {
        case 'bda':
          return ticketingAPI.bdaReplyToTicket(id, messageOrFormData);
        case 'provider':
          return ticketingAPI.providerReplyToTicket(id, messageOrFormData);
        case 'admin':
        default:
          return ticketingAPI.adminReplyToTicket(id, messageOrFormData);
      }
    }
    
    // Legacy handling for separate parameters
    switch(role) {
      case 'bda':
        return ticketingAPI.bdaReplyToTicket(id, messageOrFormData, attachment, closeTicket);
      case 'provider':
        return ticketingAPI.providerReplyToTicket(id, { message: messageOrFormData, attachment, closeTicket });
      case 'admin':
      default:
        return ticketingAPI.adminReplyToTicket(id, { message: messageOrFormData, attachment, closeTicket });
    }
  },
  
  closeTicket: (id) => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const role = userData.role || 'admin'; // Default to admin if no role found
    
    switch(role) {
      case 'bda':
        return ticketingAPI.bdaCloseTicket(id);
      case 'provider':
        return ticketingAPI.providerCloseTicket(id);
      case 'admin':
      default:
        return ticketingAPI.adminCloseTicket(id);
    }
  },
  
  reopenTicket: (id) => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const role = userData.role || 'admin'; // Default to admin if no role found
    
    switch(role) {
      case 'bda':
        return ticketingAPI.bdaReopenTicket(id);
      case 'provider':
        return ticketingAPI.providerReopenTicket(id);
      case 'admin':
      default:
        return ticketingAPI.adminReopenTicket(id);
    }
  },
  
  // Get notifications for a specific ticket
  getTicketNotifications: async (id) => {
    return await notificationApi.getTicketNotifications(id);
  },
  
  // Mark all notifications for a ticket as read
  markTicketNotificationsAsRead: async (id) => {
    try {
      const notifications = await notificationApi.getTicketNotifications(id);
      const unreadIds = notifications
        .filter(notif => !notif.is_read)
        .map(notif => notif.id);
      
      if (unreadIds.length > 0) {
        await notificationApi.bulkUpdate(unreadIds, true);
        return true;
      }
      return false;
    } catch (error) {
      console.error(`Error marking ticket ${id} notifications as read:`, error);
      return false;
    }
  }
};

// User API - General user operations across all user types
export const userAPI = {
  updateProfilePhoto: (formData) => {
    return apiClient.post('/api/user/users/profile/photo/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
      }
    });
  },
  updateProfile: (data) => {
    return apiClient.patch('/api/user/profile/', data);
  },
  getProfile: () => {
    return apiClient.get('/api/user/profile/');
  }
};

// WhatsApp Consent API - Manage user WhatsApp consent
export const userConsentAPI = {
  /**
   * Create or update WhatsApp consent for a user
   * @param {Object} data - Consent data including phone_number, consent, app_type
   * @returns {Promise} Promise resolving to consent creation/update result
   */
  createOrUpdateConsent: async (data) => {
    try {
      const response = await apiClient.post('/api/user/whatsapp-consent/', data);
      return {
        success: true,
        data: response.data,
        message: response.data.created ? 'Consent created successfully' : 'Consent updated successfully'
      };
    } catch (error) {
      console.error('Error creating/updating WhatsApp consent:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update consent',
        error: error.response?.data || error.message
      };
    }
  },

  /**
   * Get consent status for a specific phone number and app type
   * @param {string} phoneNumber - Phone number to check
   * @param {string} appType - App type to check consent for
   * @returns {Promise} Promise resolving to consent status
   */
  getConsentStatus: async (phoneNumber, appType) => {
    try {
      const response = await apiClient.get('/api/user/whatsapp-consent/', {
        params: { phone_number: phoneNumber, app_type: appType }
      });
      return {
        success: true,
        data: response.data,
        hasConsent: response.data.consent === true
      };
    } catch (error) {
      console.error('Error fetching consent status:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch consent status',
        error: error.response?.data || error.message
      };
    }
  },

  /**
   * Bulk update consent for multiple phone numbers
   * @param {Array} consentData - Array of consent objects
   * @returns {Promise} Promise resolving to bulk update result
   */
  bulkUpdateConsent: async (consentData) => {
    try {
      const promises = consentData.map(data => 
        userConsentAPI.createOrUpdateConsent(data)
      );
      
      const results = await Promise.allSettled(promises);
      
      const successful = results.filter(result => 
        result.status === 'fulfilled' && result.value.success
      ).length;
      
      const failed = results.length - successful;
      
      return {
        success: true,
        data: {
          total: results.length,
          successful,
          failed,
          results: results.map(result => 
            result.status === 'fulfilled' ? result.value : { success: false, error: result.reason }
          )
        },
        message: `Successfully updated ${successful} out of ${results.length} consent records`
      };
    } catch (error) {
      console.error('Error in bulk consent update:', error);
      return {
        success: false,
        message: 'Failed to perform bulk consent update',
        error: error.message
      };
    }
  },

  /**
   * Check if user has given consent for a specific app
   * @param {string} phoneNumber - Phone number to check
   * @param {string} appType - App type to check
   * @returns {Promise<boolean>} True if consent exists and is active
   */
  hasConsent: async (phoneNumber, appType) => {
    try {
      const result = await userConsentAPI.getConsentStatus(phoneNumber, appType);
      return result.success && result.hasConsent;
    } catch (error) {
      console.error('Error checking consent:', error);
      return false;
    }
  },

  /**
   * Revoke consent for a specific phone number and app type
   * @param {string} phoneNumber - Phone number to revoke consent for
   * @param {string} appType - App type to revoke consent for
   * @returns {Promise} Promise resolving to revocation result
   */
  revokeConsent: async (phoneNumber, appType) => {
    try {
      const response = await apiClient.post('/api/user/whatsapp-consent/', {
        phone_number: phoneNumber,
        app_type: appType,
        consent: false
      });
      return {
        success: true,
        data: response.data,
        message: 'Consent revoked successfully'
      };
    } catch (error) {
      console.error('Error revoking consent:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to revoke consent',
        error: error.response?.data || error.message
      };
    }
  }
};

// BookMyOP Schedule Management
export const updateDoctorBookMyOPSchedule = async (doctorId, scheduleData) => {
  try {
    const timeSlots = Array.isArray(scheduleData.time_slots) ? scheduleData.time_slots : [];
    
    const formattedTimeSlots = timeSlots.map(slot => {
      if (!slot.day_of_week || !slot.start_time || !slot.end_time) {
        throw new Error('Time slot is missing required fields');
      }

      return {
        day_of_week: parseInt(slot.day_of_week, 10),
        start_time: slot.start_time,
        end_time: slot.end_time,
        slot_type: 'book_my_op'
      };
    });
    
    const requestData = {
      is_active: true,
      time_slots: formattedTimeSlots
    };
    
    const response = await apiClient.put(`/api/admin/doctors/${doctorId}/update_bookmyop_schedule/`, requestData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const bulkUpdateDoctorsBookMyOPSchedules = async (formData) => {
  try {
    const timeSlotsString = formData.get('time_slots');
    const timeSlots = JSON.parse(timeSlotsString);
    
    const newFormData = new FormData();
    newFormData.append('doctor_ids', formData.get('doctor_ids'));
    
    timeSlots.forEach((slot, index) => {
      newFormData.append(`time_slots[${index}][day_of_week]`, slot.day_of_week);
      newFormData.append(`time_slots[${index}][start_time]`, slot.start_time);
      newFormData.append(`time_slots[${index}][end_time]`, slot.end_time);
      newFormData.append(`time_slots[${index}][slot_type]`, slot.slot_type);
    });
    
    newFormData.append('is_active', 'true');
    
    const response = await apiClient.post('/api/admin/doctors/bulk_update_bookmyop_schedules/', newFormData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Extract getDoctors function for direct export
export const getDoctors = serviceProviderAPI.getDoctors;

// Export services
export {
  staffManagementAPI,
  apiClient as default
};
export const getServiceProviderCalendar = async (params = {}) => {
  try {
    const response = await apiClient.get('/api/admin/doctors/calendar_view/', { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getSPServiceProviderCalendar = async (params = {}) => {
  try {
    // Try to refresh CSRF token first 
    try {
      await refreshServiceProviderCSRFToken();
    } catch (csrfError) {
      console.warn('Failed to refresh CSRF token:', csrfError);
      // Continue anyway as the interceptor might handle it
    }

    const response = await apiClient.get('/api/sp/doctor-schedules/by_service_provider/', { 
      params,
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    });
    
    if (!response.data) {
      throw new Error('No data received from the server');
    }
    
    return response.data;
  } catch (error) {
    console.error('Error fetching service provider calendar:', error);
    console.error('Error details:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      headers: error.response?.headers
    });
    throw error;
  }
};

// BDA Doctor API
export const bdaDoctorAPI = {
  getAll: (page = 1, pageSize = 10, filters = {}) => {
    const params = { 
      page, 
      page_size: pageSize,
      ...filters 
    };
    
    // Add symptom filtering support
    if (filters.symptoms) {
      params.symptoms = Array.isArray(filters.symptoms) ? filters.symptoms.join(',') : filters.symptoms;
    }
    
    return paginationUtils.getPaginatedData('/api/bda/doctors/', page, pageSize, params);
  },
  
  getOne: (id) => apiClient.get(`/api/bda/doctors/${id}/`),
  
  create: (formData) => {
    // For multipart/form-data with files
    if (formData instanceof FormData) {
      return apiClient.post('/api/bda/doctors/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    }
    return apiClient.post('/api/bda/doctors/', formData);
  },
  
  update: (id, formData) => {
    // For multipart/form-data with files
    if (formData instanceof FormData) {
      return apiClient.put(`/api/bda/doctors/${id}/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    }
    return apiClient.put(`/api/bda/doctors/${id}/`, formData);
  },
  
  patch: (id, formData) => {
    // For multipart/form-data with files
    if (formData instanceof FormData) {
      return apiClient.patch(`/api/bda/doctors/${id}/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    }
    return apiClient.patch(`/api/bda/doctors/${id}/`, formData);
  },
  
  delete: (id) => apiClient.delete(`/api/bda/doctors/${id}/`),
  
  toggleActive: (id) => apiClient.post(`/api/bda/doctors/${id}/toggle-active/`),
  
  // Doctor symptom management
  updateSymptoms: (id, symptomIds) => apiClient.patch(`/api/bda/doctors/${id}/`, {
    area_of_expertise: symptomIds
  }),
  getSymptoms: (id) => apiClient.get(`/api/bda/doctors/${id}/`).then(response => 
    response.data.area_of_expertise || []
  ),
  addSymptoms: (id, symptomIds) => apiClient.patch(`/api/bda/doctors/${id}/`, {
    area_of_expertise: symptomIds
  }),
  removeSymptoms: (id, symptomIds) => apiClient.patch(`/api/bda/doctors/${id}/`, {
    area_of_expertise: symptomIds
  })
};

// BDA Doctor Schedule API
export const bdaDoctorScheduleAPI = {
  getAll: (page = 1, pageSize = 10, filters = {}) => 
    paginationUtils.getPaginatedData('/api/bda/doctor-schedules/', page, pageSize, filters),
  
  getOne: (id) => apiClient.get(`/api/bda/doctor-schedules/${id}/`),
  
  create: (formData) => apiClient.post('/api/bda/doctor-schedules/', formData),
  
  update: (id, formData) => apiClient.put(`/api/bda/doctor-schedules/${id}/`, formData),
  
  delete: (id) => apiClient.delete(`/api/bda/doctor-schedules/${id}/`),
  
  addSlot: (scheduleId, slotData) => 
    apiClient.post(`/api/bda/doctor-schedule/${scheduleId}/add-slot/`, slotData),
  
  removeSlot: ( slotId) => 
    apiClient.post(`/api/bda/doctor-schedule/${slotId}/remove-slot/`, { slot_id: slotId }),
  
  updateSlot: (scheduleId, slotId, slotData) => 
    apiClient.post(`/api/bda/doctor-schedule/${scheduleId}/update-slot/`, {
      slot_id: slotId,
      ...slotData
    }),
    
  getByDoctor: (doctorId) => 
    apiClient.get('/api/bda/doctor-schedule/', { params: { doctor: doctorId } }),
    
  updateBookMyOPSchedule: (scheduleData) => 
    apiClient.put('/api/bda/doctor-schedule/update_bookmyop/', scheduleData),
    
  updateEasyOPSchedule: (scheduleData) => 
    apiClient.put('/api/bda/doctor-schedule/update_easyop/', scheduleData)
};

export const refreshAdminCSRFToken = async () => {
  try {
    const response = await apiClient.get('/api/admin/auth/csrf/', { withCredentials: true });
    return response.data.token;
  } catch (error) {
    throw error;
  }
};

export const refreshServiceProviderCSRFToken = async () => {
  try {
    const response = await apiClient.get('/api/sp/auth/csrf/', { withCredentials: true });
    return response.data.token;
  } catch (error) {
    throw error;
  }
};

export const refreshBdaCSRFToken = async () => {
  try {
    const response = await apiClient.get('/api/bda/auth/csrf/', { withCredentials: true });
    return response.data.token;
  } catch (error) {
    throw error;
  }
};

export const getServiceProviders = async (page = 1, pageSize = 10, filters = {}) => {
  try {
    const response = await apiClient.get('/api/admin/service-providers/', {
      params: {
        page,
        page_size: pageSize,
        ...filters
      }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createServiceProvider = async (formData) => {
  try {
    const response = await apiClient.post('/api/admin/service-providers/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createDoctor = async (formData) => {
  try {
    const response = await apiClient.post('/api/admin/doctors/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const removeTimeSlot = async (scheduleId, slotId) => {
  try {
    const response = await apiClient.delete(`/api/admin/schedules/${scheduleId}/slots/${slotId}/`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getSpecialties = async () => {
  try {
    // Try to get CSRF token, but continue even if it fails
    try {
      await refreshCSRFToken();
    } catch (error) {
      console.warn('Failed to get CSRF token, continuing without it');
    }
    
    const response = await apiClient.get('/api/sp/specialties/');
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const registerServiceProvider = async (formData) => {
  try {
    await refreshCSRFToken();
    const response = await apiClient.post('/api/sp/register/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// BookMyOP Patient Admin APIs
export const bookMyOPPatientAdminAPI = {
  getAll: async (page = 1, pageSize = 10, filters = {}) => {
    try {
      // Get CSRF token before making request
      await refreshAdminCSRFToken();
      
      const params = {
        page,
        page_size: pageSize,
        ...filters
      };
      
      // Remove undefined values from params
      Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);
      
      // Get auth token
      const token = localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await apiClient.get('/api/admin/bookmyop-patients/', { 
        params,
        headers: {
          'Authorization': token.startsWith('Bearer ') ? token : `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching BookMyOP patients:', error);
      throw error;
    }
  },

  getOne: async (patientId) => {
    try {
      // Get CSRF token before request
      await refreshAdminCSRFToken();
      
      // Get auth token
      const token = localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await apiClient.get(`/api/admin/bookmyop-patients/${patientId}/`, {
        headers: {
          'Authorization': token.startsWith('Bearer ') ? token : `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching BookMyOP patient ${patientId}:`, error);
      throw error;
    }
  },

  update: async (patientId, data) => {
    try {
      // Get CSRF token before update
      await refreshAdminCSRFToken();
      
      // Get auth token
      const token = localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await apiClient.patch(`/api/admin/bookmyop-patients/${patientId}/`, data, {
        headers: {
          'Authorization': token.startsWith('Bearer ') ? token : `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      console.error(`Error updating BookMyOP patient ${patientId}:`, error);
      throw error;
    }
  },

  toggleActiveStatus: async (patientId) => {
    try {
      // Get CSRF token before status toggle
      await refreshAdminCSRFToken();
      
      // Get auth token
      const token = localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await apiClient.post(`/api/admin/bookmyop-patients/${patientId}/toggle_active_status/`, {}, {
        headers: {
          'Authorization': token.startsWith('Bearer ') ? token : `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      console.error(`Error toggling BookMyOP patient ${patientId} status:`, error);
      throw error;
    }
  },

  getAppointments: async (patientId, page = 1, pageSize = 10) => {
    try {
      // Get CSRF token before request
      await refreshAdminCSRFToken();
      
      // Get auth token
      const token = localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const params = new URLSearchParams({
        page,
        page_size: pageSize
      });
      
      const response = await apiClient.get(`/api/admin/bookmyop-patients/${patientId}/appointments/?${params}`, {
        headers: {
          'Authorization': token.startsWith('Bearer ') ? token : `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching appointments for BookMyOP patient ${patientId}:`, error);
      throw error;
    }
  },

  getStatistics: async (patientId) => {
    try {
      // Get CSRF token before request
      await refreshAdminCSRFToken();
      
      // Get auth token
      const token = localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await apiClient.get(`/api/admin/bookmyop-patients/${patientId}/statistics/`, {
        headers: {
          'Authorization': token.startsWith('Bearer ') ? token : `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching statistics for BookMyOP patient ${patientId}:`, error);
      throw error;
    }
  }
};

// Walk-in Appointment APIs
export const walkInAppointmentAPI = {
  create: async (appointmentData) => {
    try {
      await refreshServiceProviderCSRFToken();
      const response = await apiClient.post('/api/sp/appointments/create_walk_in/', appointmentData);
      return response.data;
    } catch (error) {
      console.error('Error creating walk-in appointment:', error);
      throw error;
    }
  },

  assignDoctor: async (appointmentId, doctorId) => {
    try {
      await refreshServiceProviderCSRFToken();
      const response = await apiClient.post(`/api/sp/appointments/${appointmentId}/assign_doctor/`, {
        doctor: doctorId
      });
      return response.data;
    } catch (error) {
      console.error('Error assigning doctor to walk-in appointment:', error);
      throw error;
    }
  },

  getAll: async (page = 1, pageSize = 10, filters = {}) => {
    try {
      const params = {
        page,
        page_size: pageSize,
        is_walk_in: true,
        ...filters
      };
      
      // Remove undefined values from params
      Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);
      
      const response = await apiClient.get('/api/sp/appointments/', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching walk-in appointments:', error);
      throw error;
    }
  }
};

// Walk-in Appointment Admin APIs
export const walkInAppointmentAdminAPI = {
  getAll: async (page = 1, pageSize = 10, filters = {}) => {
    try {
      const params = {
        page,
        page_size: pageSize,
        ...filters
      };
      
      // Remove undefined values from params
      Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);
      
      const response = await apiClient.get('/api/admin/walk-in-appointments/', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching walk-in appointments:', error);
      throw error;
    }
  },

  getOne: async (appointmentId) => {
    try {
      const response = await apiClient.get(`/api/admin/walk-in-appointments/${appointmentId}/`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching walk-in appointment ${appointmentId}:`, error);
      throw error;
    }
  },

  create: async (data) => {
    try {
      // Get CSRF token before creating
      await refreshAdminCSRFToken();
      
      const response = await apiClient.post('/api/admin/walk-in-appointments/', data);
      return response.data;
    } catch (error) {
      console.error('Error creating walk-in appointment:', error);
      throw error;
    }
  },

  update: async (appointmentId, data) => {
    try {
      // Get CSRF token before update
      await refreshAdminCSRFToken();
      
      const response = await apiClient.put(`/api/admin/walk-in-appointments/${appointmentId}/`, data);
      return response.data;
    } catch (error) {
      console.error(`Error updating walk-in appointment ${appointmentId}:`, error);
      throw error;
    }
  },

  delete: async (appointmentId) => {
    try {
      // Get CSRF token before deletion
      await refreshAdminCSRFToken();
      
      const response = await apiClient.delete(`/api/admin/walk-in-appointments/${appointmentId}/`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting walk-in appointment ${appointmentId}:`, error);
      throw error;
    }
  },

  getToday: async () => {
    try {
      const response = await apiClient.get('/api/admin/walk-in-appointments/today/');
      return response.data;
    } catch (error) {
      console.error('Error fetching today\'s walk-in appointments:', error);
      throw error;
    }
  },

  getStatistics: async () => {
    try {
      const response = await apiClient.get('/api/admin/walk-in-appointments/statistics/');
      return response.data;
    } catch (error) {
      console.error('Error fetching walk-in appointment statistics:', error);
      throw error;
    }
  }
};
/**
 * Get all specialties with doctor counts for the current service provider
 * @returns {Promise} Response with specialties and their doctor counts
 */
export const getDoctorSpecialties = async () => {
  try {
    const response = await apiClient.get('/api/sp/doctor-specialties/');
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Handles API errors by extracting relevant error information
 * @param {Error} error - The error object from axios
 * @returns {Error} A formatted error with a meaningful message
 */
const handleApiError = (error) => {
  if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    const errorMessage = error.response.data?.detail || 
                        error.response.data?.message || 
                        error.response.data?.error ||
                        error.response.data?.patient?.user?.mobile_number ||
                        'An error occurred while processing your request';
    return new Error(errorMessage);
  } else if (error.request) {
    // The request was made but no response was received
    return new Error('No response received from server');
  } else {
    // Something happened in setting up the request that triggered an Error
    return new Error(error.message || 'An unexpected error occurred');
  }
};

// Service Provider Patient Management
export const getServiceProviderPatients = async (params = {}) => {
  const response = await apiClient.get('/api/sp/patients/', { params });
  return response.data;
};

export const getServiceProviderPatientDetails = async (patientId,patientType) => {
  const response = await apiClient.get(`/api/sp/patients/${patientId}/details/?patient_type=${patientType}`);
  return response.data;
};

export const getServiceProviderPatientAppointments = async (patientId) => {
  const response = await apiClient.get(`/api/sp/patients/${patientId}/appointments/`);
  return response.data;
};

export const getServiceProviderPatientDocuments = async (patientId,patientType) => {
  const response = await apiClient.get(`/api/sp/patients/${patientId}/documents/?patient_type=${patientType}`);
  return response.data;
};

export const getServiceProviderPatientStatistics = async () => {
  const response = await apiClient.get('/api/sp/patients/statistics/');
  return response.data;
};

export const adminEasyOPAPI = {
  getAll: async (page = 1, pageSize = 10, filters = {}) => {
    try {
      const params = { page, page_size: pageSize, ...filters };
      const response = await apiClient.get('/api/admin/easyop-patients/', { params });
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  getOne: async (id) => {
    try {
      const response = await apiClient.get(`/api/admin/easyop-patients/${id}/`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  create: async (userData) => {
    try {
      const response = await apiClient.post('/api/admin/easyop-patients/', userData);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  update: async (id, userData) => {
    try {
      const response = await apiClient.put(`/api/admin/easyop-patients/${id}/`, userData);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  delete: async (id) => {
    try {
      const response = await apiClient.delete(`/api/admin/easyop-patients/${id}/`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  sendOTP: async (phone) => {
    try {
      const response = await apiClient.post('/api/admin/auth/send-otp/', { phone });
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  verifyOTP: async (phone, otp) => {
    try {
      const response = await apiClient.post('/api/admin/auth/verify-otp/', { phone, otp });
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  getById: async (id) => {
    try {
      const response = await apiClient.get(`/api/admin/easyop-patients/${id}/`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  getStatistics: async (id) => {
    try {
      const response = await apiClient.get(`/api/admin/easyop-patients/${id}/statistics/`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  getCards: async (id) => {
    try {
      const response = await apiClient.get(`/api/admin/easyop-patients/${id}/cards/`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },
};

// EasyOP Card Tariff API Client
export const easyOPCardTariffAPI = {
  getAll: async (page = 1, pageSize = 10) => {
    try {
      const response = await apiClient.get(`/api/admin/easyop-card-tariffs/`, {
        params: {
          page,
          page_size: pageSize
        }
      });
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  getActive: async () => {
    try {
      const response = await apiClient.get('/api/admin/easyop-card-tariffs/active/');
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  getById: async (id) => {
    try {
      const response = await apiClient.get(`/api/admin/easyop-card-tariffs/${id}/`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  create: async (data) => {
    try {
      const response = await apiClient.post('/api/admin/easyop-card-tariffs/', data);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  update: async (id, data) => {
    try {
      const response = await apiClient.put(`/api/admin/easyop-card-tariffs/${id}/`, data);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  delete: async (id) => {
    try {
      await apiClient.delete(`/api/admin/easyop-card-tariffs/${id}/`);
    } catch (error) {
      handleApiError(error);
    }
  },

  toggleActive: async (id) => {
    try {
      const response = await apiClient.post(`/api/admin/easyop-card-tariffs/${id}/toggle-active/`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  }
};

// Service Provider EasyOP Appointment API
export const serviceProviderEasyOPAPI = {
  // Get all EasyOP appointments with pagination and filters
  getAppointments: async (page = 1, pageSize = 20, filters = {}) => {
    try {
      const params = {
        page,
        page_size: pageSize,
        ...filters,
        _t: new Date().getTime() // Cache buster
      };
      
      const response = await apiClient.get('/api/sp/easy-op/appointments/', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching EasyOP appointments:', error);
      throw error;
    }
  },

  // Get a single EasyOP appointment
  getAppointment: async (id) => {
    try {
      const response = await apiClient.get(`/api/sp/easy-op/appointments/${id}/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching EasyOP appointment:', error);
      throw error;
    }
  },

  // Update appointment status
  updateStatus: async (id, status) => {
    try {
      const response = await apiClient.post(`/api/sp/easy-op/appointments/${id}/update_status/`, { status });
      return response.data;
    } catch (error) {
      console.error('Error updating EasyOP appointment status:', error);
      throw error;
    }
  },

  // Cancel EasyOP appointment with reason and notes
  cancelAppointment: async (id, cancellationData) => {
    try {
      const response = await apiClient.post(`/api/sp/easyop-appointments/${id}/cancel/`, cancellationData);
      return response.data;
    } catch (error) {
      console.error('Error cancelling EasyOP appointment:', error);
      throw error;
    }
  },

  // Get appointment statistics
  getStatistics: async () => {
    try {
      const response = await apiClient.get('/api/sp/easy-op/appointments/statistics/');
      return response.data;
    } catch (error) {
      console.error('Error fetching EasyOP appointment statistics:', error);
      throw error;
    }
  }
};
export const easyOPCardsApi = {
  getPrimaryMembers: async (params = {}) => {
    try {
      const response = await apiClient.get('/api/bda/easyop-cards/', { params });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },
  sendOTP: async (data) => {
    try {
      const response = await apiClient.post('/api/bda/easyop-cards/send_otp/', data);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },
  verifyOTP: async (data) => {
    try {
      const response = await apiClient.post('/api/bda/easyop-cards/verify_otp/', data);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  getFamilyCards: async (familyId) => {
    try {
      const response = await apiClient.get(`/api/bda/easyop-cards/family_cards/`, {
        params: { family_id: familyId }
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  createFamily: async (data) => {
    try {
      const response = await apiClient.post('/api/bda/easyop-cards/', data);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  addFamilyMember: async (data) => {
    try {
      const response = await apiClient.post('/api/bda/easyop-cards/add_member/', data);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  updatePaymentStatus: async (cardId, data) => {
    try {
      const response = await apiClient.patch(`/api/bda/easyop-cards/${cardId}/update_payment_status/`, data);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // New method for updating card details
  updateCard: async (cardId, data) => {
    try {
      const response = await apiClient.patch(`/api/bda/easyop-cards/${cardId}/`, data);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // New method for full update of card details
  updateCardFull: async (cardId, data) => {
    try {
      const response = await apiClient.put(`/api/bda/easyop-cards/${cardId}/`, data);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  getTariffs: async () => {
    try {
      const response = await apiClient.get('/api/bda/easyop-tariffs/');
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Get family details
  getFamilyDetails: async (familyId) => {
    try {
      const response = await apiClient.get(`/api/bda/easyop-cards/family/${familyId}/`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Get auto-fill data for new family member
  getAutoFillData: async (familyId, relationship) => {
    try {
      const response = await apiClient.get(`/api/bda/easyop-cards/family/${familyId}/auto-fill/`, {
        params: { relationship }
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Validate family member addition
  validateFamilyAddition: async (data) => {
    try {
      const response = await apiClient.post('/api/bda/easyop-cards/family/validate-addition/', data);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Bulk create family members
  bulkCreateMembers: async (data) => {
    try {
      const response = await apiClient.post('/api/bda/easyop-cards/family/bulk-create/', data);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }
};
export const EasyopManagement = {
  registerPrimaryMember: async (data) => {
    try {
      const response = await apiClient.post('/api/bda/bda-easyop-family/register-primary/', data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  enrollDependent: async (data) => {
    try {
      const response = await apiClient.post('/api/bda/bda-easyop-family/enroll-dependent/', data);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  lookupPrimaryByMobile: async (mobile) => {
    try {
      const response = await apiClient.get('/api/bda/bda-easyop-family/lookup-primary/', {
        params: { mobile }
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  getFamilyOverview: async (familyId) => {
    try {
      const response = await apiClient.get('/api/bda/bda-easyop-family/family-overview/', {
        params: { family_id: familyId }
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  getAllCards: async (params = {}) => {
    try {
      const response = await apiClient.get('/api/bda/easyop-cards/all_cards/', {
        params: {
          payment_status: params.paymentStatus,
          family_position: params.familyPosition,
          search: params.search,
          page: params.page,
          page_size: params.pageSize || 10
        }
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }
};

export const sendPatientCommunication = async (patientType, patientId, data) => {
  try {
    // Create a new FormData instance
    const formData = new FormData();
    
    // Add the patient type and id
    formData.append('patient_type', patientType);
    formData.append('patient_id', patientId);
    
    // If data is FormData, append its entries to our formData
    if (data instanceof FormData) {
      for (let [key, value] of data.entries()) {
        formData.append(key, value);
      }
    }
    
    const response = await apiClient.post(`/api/sp/tickets/message_patient/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const getPatientCommunications = async (patientType, patientId) => {
  try {
    const response = await apiClient.get(`/api/sp/tickets/`, {
      params: {
        patient_type: patientType,
        patient_id: patientId
      }
    });
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

// Promo Code APIs
export const getPromoCodes = async (params = {}) => {
  try {
    const response = await apiClient.get('/api/admin/promo-codes/', { params });
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const getPromoCode = async (id) => {
  try {
    const response = await apiClient.get(`/api/admin/promo-codes/${id}/`);
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const createPromoCode = async (data) => {
  try {
    const response = await apiClient.post('/api/admin/promo-codes/', data);
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const updatePromoCode = async (id, data) => {
  try {
    const response = await apiClient.patch(`/api/admin/promo-codes/${id}/`, data);
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const deletePromoCode = async (id) => {
  try {
    const response = await apiClient.delete(`/api/admin/promo-codes/${id}/`);
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const togglePromoCodeActive = async (id) => {
  try {
    const response = await apiClient.post(`/api/admin/promo-codes/${id}/toggle_active/`);
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const getActivePromoCodes = async () => {
  try {
    const response = await apiClient.get('/api/admin/promo-codes/active/');
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

// Admin Advertisement APIs
export const getAdvertisements = async (params = {}) => {
  try {
    const response = await apiClient.get('/api/admin/advertisements/', { params });
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const getAdvertisement = async (id) => {
  try {
    const response = await apiClient.get(`/api/admin/advertisements/${id}/`);
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const createAdvertisement = async (formData) => {
  try {
    const response = await apiClient.post('/api/admin/advertisements/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const updateAdvertisement = async (id, formData) => {
  try {
    const response = await apiClient.patch(`/api/admin/advertisements/${id}/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const deleteAdvertisement = async (id) => {
  try {
    const response = await apiClient.delete(`/api/admin/advertisements/${id}/`);
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const changeAdvertisementStatus = async (id, data) => {
  try {
    const response = await apiClient.post(`/api/admin/advertisements/${id}/change_status/`, data);
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

// Admin Advertisement Cost APIs
export const getAdvertisementCosts = async () => {
  try {
    const response = await apiClient.get('/api/admin/advertisement-costs/');
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const updateAdvertisementCosts = async (data) => {
  try {
    const response = await apiClient.patch('/api/admin/advertisement-costs/1/', data);
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const setAllAdvertisementCosts = async (data) => {
  try {
    const response = await apiClient.put('/api/admin/advertisement-costs/1/', data);
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

// Service Provider Advertisement APIs
export const getSPAdvertisements = async (params = {}) => {
  try {
    const response = await apiClient.get('/api/sp/advertisements/', { params });
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const getSPAdvertisement = async (id) => {
  try {
    const response = await apiClient.get(`/api/sp/advertisements/${id}/`);
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const createSPAdvertisement = async (formData) => {
  try {
    const response = await apiClient.post('/api/sp/advertisements/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const updateSPAdvertisement = async (id, data) => {
  try {
    await refreshServiceProviderCSRFToken();
    // Handle FormData for image upload
    if (data instanceof FormData) {
      const response = await apiClient.patch(`/api/sp/advertisements/${id}/`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    }
    // Handle regular data
    const response = await apiClient.patch(`/api/sp/advertisements/${id}/`, data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

export const resendSPAdvertisementForApproval = async (id, data) => {
  try {
    await refreshServiceProviderCSRFToken();
    const response = await apiClient.patch(`/api/sp/advertisements/${id}/resend_for_approval/`, data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

export const updateSPAdvertisementPaymentStatus = async (id, paymentStatus) => {
  try {
    await refreshServiceProviderCSRFToken();
    const response = await apiClient.patch(`/api/sp/advertisements/${id}/update_payment_status/`, {
      payment_status: paymentStatus
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

export const deleteSPAdvertisement = async (id) => {
  try {
    const response = await apiClient.delete(`/api/sp/advertisements/${id}/`);
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const cancelSPAdvertisementRequest = async (id) => {
  try {
    const response = await apiClient.post(`/api/sp/advertisements/${id}/cancel_request/`);
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const getSPAdvertisementCostEstimate = async (id) => {
  try {
    const response = await apiClient.get(`/api/sp/advertisements/${id}/cost_estimate/`);
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const calculateSPAdvertisementEstimate = async (data) => {
   // Handle FormData for calculation with form data
   if (data instanceof FormData) {
    return apiClient.post('/api/sp/advertisements/calculate_estimate/', data, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
  return apiClient.post('/api/sp/advertisements/calculate_estimate/', data);
}

// Doctor Date Break API
export const doctorDateBreakAPI = {
  // Admin methods
  adminGetAll: (page = 1, pageSize = 10, filters = {}) => 
    paginationUtils.getPaginatedData('/api/admin/doctor-date-breaks/', page, pageSize, filters),
  adminGetOne: (id) => apiClient.get(`/api/admin/doctor-date-breaks/${id}/`),
  adminCreate: (data) => apiClient.post('/api/admin/doctor-date-breaks/', data),
  adminUpdate: (id, data) => apiClient.put(`/api/admin/doctor-date-breaks/${id}/`, data),
  adminDelete: (id) => apiClient.delete(`/api/admin/doctor-date-breaks/${id}/`),
  adminGetByDoctor: async (doctorId) => {
    const response = await apiClient.get('/api/admin/doctor-date-breaks/', { 
      params: { doctor: doctorId } 
    });
    return paginationUtils.getPaginationInfo(response);
  },

  // BDA methods
  bdaGetAll: (page = 1, pageSize = 10, filters = {}) => 
    paginationUtils.getPaginatedData('/api/bda/doctor-date-breaks/', page, pageSize, filters),
  bdaGetOne: (id) => apiClient.get(`/api/bda/doctor-date-breaks/${id}/`),
  bdaCreate: (data) => apiClient.post('/api/bda/doctor-date-breaks/', data),
  bdaUpdate: (id, data) => apiClient.put(`/api/bda/doctor-date-breaks/${id}/`, data),
  bdaDelete: (id) => apiClient.delete(`/api/bda/doctor-date-breaks/${id}/`),
  bdaGetByDoctor: async (doctorId) => {
    const response = await apiClient.get('/api/bda/doctor-date-breaks/', { 
      params: { doctor: doctorId } 
    });
    return paginationUtils.getPaginationInfo(response);
  },

  // Service Provider methods
  spGetAll: (page = 1, pageSize = 10, filters = {}) => 
    paginationUtils.getPaginatedData('/api/sp/doctor-date-breaks/', page, pageSize, filters),
  spGetOne: (id) => apiClient.get(`/api/sp/doctor-date-breaks/${id}/`),
  spCreate: (data) => apiClient.post('/api/sp/doctor-date-breaks/', data),
  spUpdate: (id, data) => apiClient.put(`/api/sp/doctor-date-breaks/${id}/`, data),
  spCreateBulk: (data) => apiClient.post('/api/sp/doctor-date-breaks/bulk_create/', data),
  spDelete: (id) => apiClient.delete(`/api/sp/doctor-date-breaks/${id}/`),
  spGetByDoctor: async (doctorId) => {
    const response = await apiClient.get('/api/sp/doctor-date-breaks/', { 
      params: { doctor: doctorId } 
    });
    return paginationUtils.getPaginationInfo(response);
  },

  // Common methods - detect user role and route to appropriate endpoint
  getAll: (page = 1, pageSize = 10, filters = {}) => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const userRole = localStorage.getItem('userRole') || '';
    const role = userData.role || userRole || 'admin';
    
    switch(role) {
      case 'bda':
        return doctorDateBreakAPI.bdaGetAll(page, pageSize, filters);
      case 'service-provider':
      case 'sp_staff':
        return doctorDateBreakAPI.spGetAll(page, pageSize, filters);
      case 'admin':
      default:
        return doctorDateBreakAPI.adminGetAll(page, pageSize, filters);
    }
  },

  getOne: (id) => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const userRole = localStorage.getItem('userRole') || '';
    const role = userData.role || userRole || 'admin';
    
    switch(role) {
      case 'bda':
        return doctorDateBreakAPI.bdaGetOne(id);
      case 'service-provider':
      case 'sp_staff':
        return doctorDateBreakAPI.spGetOne(id);
      case 'admin':
      default:
        return doctorDateBreakAPI.adminGetOne(id);
    }
  },

  create: (data) => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const userRole = localStorage.getItem('userRole') || '';
    const role = userData.role || userRole || 'admin';
    
    switch(role) {
      case 'bda':
        return doctorDateBreakAPI.bdaCreate(data);
      case 'service-provider':
      case 'sp_staff':
        return doctorDateBreakAPI.spCreate(data);
      case 'admin':
      default:
        return doctorDateBreakAPI.adminCreate(data);
    }
  },

  update: (id, data) => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const userRole = localStorage.getItem('userRole') || '';
    const role = userData.role || userRole || 'admin';
    
    switch(role) {
      case 'bda':
        return doctorDateBreakAPI.bdaUpdate(id, data);
      case 'service-provider':
      case 'sp_staff':
        return doctorDateBreakAPI.spUpdate(id, data);
      case 'admin':
      default:
        return doctorDateBreakAPI.adminUpdate(id, data);
    }
  },

  delete: (id) => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const userRole = localStorage.getItem('userRole') || '';
    const role = userData.role || userRole || 'admin';
    
    switch(role) {
      case 'bda':
        return doctorDateBreakAPI.bdaDelete(id);
      case 'service-provider':
      case 'sp_staff':
        return doctorDateBreakAPI.spDelete(id);
      case 'admin':
      default:
        return doctorDateBreakAPI.adminDelete(id);
    }
  },

  getByDoctor: (doctorId) => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const userRole = localStorage.getItem('userRole') || '';
    const role = userData.role || userRole || 'admin';
    
    switch(role) {
      case 'bda':
        return doctorDateBreakAPI.bdaGetByDoctor(doctorId);
      case 'service-provider':
      case 'sp_staff':
        return doctorDateBreakAPI.spGetByDoctor(doctorId);
      case 'admin':
      default:
        return doctorDateBreakAPI.adminGetByDoctor(doctorId);
    }
  }
};

// Service Provider Verification Admin API (minimal endpoints)
export const serviceProviderVerificationAPI = {
  // List all verifications (no filters/pagination for now)
  getAll: () => apiClient.get('/api/admin/service-provider-verifications/'),

  // Retrieve a single verification by ID
  getOne: (id) => apiClient.get(`/api/admin/service-provider-verifications/?service_provider=${id}`),

  // Create a verification (supports FormData for related_file uploads)
  create: (data) => {
    if (data instanceof FormData || (data && data.related_file)) {
      return apiClient.post('/api/admin/service-provider-verifications/', data);
    }
    return apiClient.post('/api/admin/service-provider-verifications/', data);
  },

  // Update a verification (PATCH; supports FormData for related_file uploads)
  update: (id, data) => {
    if (data instanceof FormData || (data && data.related_file)) {
      return apiClient.patch(`/api/admin/service-provider-verifications/${id}/`, data);
    }
    return apiClient.patch(`/api/admin/service-provider-verifications/${id}/`, data);
  },

  // Delete a verification
  delete: (id) => apiClient.delete(`/api/admin/service-provider-verifications/${id}/`),

  // Approve a verification with optional comments
  approve: (id, comments) => 
    apiClient.post(`/api/admin/service-provider-verifications/${id}/approve/`, comments ? { comments } : {}),

  // Reject a verification with optional comments
  reject: (id, comments) => 
    apiClient.post(`/api/admin/service-provider-verifications/${id}/reject/`, comments ? { comments } : {}),

  // Reset verification status back to pending
  resetToPending: (id) => 
    apiClient.patch(`/api/admin/service-provider-verifications/${id}/reset_to_pending/`),
};

// BDA Verification Admin API (minimal endpoints)
export const bdaVerificationAPI = {
  // List all BDA verifications
  getAll: () => apiClient.get('/api/admin/bda-verifications/'),

  // Retrieve a single BDA verification by ID
  getOne: (id) => apiClient.get(`/api/admin/bda-verifications/?bda=${id}`),

  // Create a BDA verification (JSON payload) - only send required fields
  create: (data) => {
    const payload = {
      bda: data?.bda,
      verification_type: data?.verification_type,
    };
    return apiClient.post('/api/admin/bda-verifications/', payload);
  },

  // Update a BDA verification (PATCH)
  update: (id, data) => apiClient.patch(`/api/admin/bda-verifications/${id}/`, data),

  // Delete a BDA verification
  delete: (id) => apiClient.delete(`/api/admin/bda-verifications/${id}/`),

  // Convenience helpers using PATCH to set verification_status
  approve: (id) => apiClient.patch(`/api/admin/bda-verifications/${id}/`, { verification_status: 'approved' }),
  reject: (id) => apiClient.patch(`/api/admin/bda-verifications/${id}/`, { verification_status: 'rejected' }),
  resetToPending: (id) => apiClient.patch(`/api/admin/bda-verifications/${id}/`, { verification_status: 'pending' }),
};

// Organization Types API
export const getOrganizationTypes = async (searchQuery = '') => {
  try {
    const response = await apiClient.get(`/api/user/organization-types/${searchQuery ? `?search=${searchQuery}` : ''}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// Service Provider Types API (with organization filtering)
export const getServiceProviderTypes = async (searchQuery = '', orgTypeId = null) => {
  try {
    const queryParams = new URLSearchParams();
    if (searchQuery) queryParams.append('search', searchQuery);
    if (orgTypeId) queryParams.append('org_type', orgTypeId);
    
    const queryString = queryParams.toString();
    const response = await apiClient.get(`/api/user/provider-types/${queryString ? `?${queryString}` : ''}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// Password Change API Functions
export const passwordChangeAPI = {
  /**
   * Request password change and send OTP via WhatsApp
   * @param {string} email - User's email address
   * @param {string} phoneNumber - User's phone number
   * @returns {Promise} Promise resolving to OTP request response
   */
  requestPasswordChangeOTP: async (email, phoneNumber) => {
    try {
      const response = await apiClient.post('/api/user/users/password/change-request/', {
        email,
        phone_number: phoneNumber
      });
      return response.data;
    } catch (error) {
      console.error('Error requesting password change OTP:', error);
      throw error;
    }
  },

  /**
   * Verify OTP for password change
   * @param {string} email - User's email address
   * @param {string} phoneNumber - User's phone number
   * @param {string} otp - 6-digit OTP code
   * @returns {Promise} Promise resolving to OTP verification response
   */
  verifyPasswordChangeOTP: async (email, phoneNumber, otp) => {
    try {
      const response = await apiClient.post('/api/user/users/password/verify-otp/', {
        email,
        phone_number: phoneNumber,
        otp
      });
      return response.data;
    } catch (error) {
      console.error('Error verifying password change OTP:', error);
      throw error;
    }
  },

  /**
   * Update password after successful OTP verification
   * @param {string} email - User's email address
   * @param {string} phoneNumber - User's phone number
   * @param {string} otp - 6-digit OTP code
   * @param {string} newPassword - New password
   * @param {string} confirmPassword - Password confirmation
   * @returns {Promise} Promise resolving to password update response
   */
  updatePassword: async (email, phoneNumber, otp, newPassword, confirmPassword) => {
    try {
      const response = await apiClient.post('/api/user/users/password/update/', {
        email,
        phone_number: phoneNumber,
        otp,
        new_password: newPassword,
        confirm_password: confirmPassword
      });
      return response.data;
    } catch (error) {
      console.error('Error updating password:', error);
      throw error;
    }
  },

  // ========================================
  // APPOINTMENT CANCELLATION APIs
  // ========================================

  /**
   * Cancel an appointment (Admin)
   * @param {number} appointmentId - ID of the appointment to cancel
   * @param {Object} cancellationData - Cancellation details
   * @param {string} cancellationData.cancellation_reason - Reason for cancellation
   * @param {string} cancellationData.cancellation_type - Type of cancellation
   * @param {string} [cancellationData.cancellation_notes] - Additional notes
   * @returns {Promise} Promise resolving to cancellation response
   */
  cancelAppointmentAdmin: async (appointmentId, cancellationData) => {
    try {
      const response = await apiClient.post(`/api/admin/appointments/${appointmentId}/cancel/`, cancellationData);
      return response.data;
    } catch (error) {
      console.error('Error cancelling appointment (Admin):', error);
      throw error;
    }
  },

  /**
   * Cancel an appointment (Service Provider)
   * @param {number} appointmentId - ID of the appointment to cancel
   * @param {Object} cancellationData - Cancellation details
   * @param {string} cancellationData.cancellation_reason - Reason for cancellation
   * @param {string} cancellationData.cancellation_type - Type of cancellation
   * @param {string} [cancellationData.cancellation_notes] - Additional notes
   * @returns {Promise} Promise resolving to cancellation response
   */
  cancelAppointmentServiceProvider: async (appointmentId, cancellationData) => {
    try {
      const response = await apiClient.post(`/api/sp/appointments/${appointmentId}/cancel/`, cancellationData);
      return response.data;
    } catch (error) {
      console.error('Error cancelling appointment (Service Provider):', error);
      throw error;
    }
  },

  /**
   * Get cancellation reasons options
   * @returns {Array} Array of cancellation reason options
   */
  getCancellationReasons: () => {
    return [
      { value: 'patient_requested', label: 'Patient Requested' },
      { value: 'doctor_unavailable', label: 'Doctor Unavailable' },
      { value: 'emergency', label: 'Emergency' },
      { value: 'weather_conditions', label: 'Weather Conditions' },
      { value: 'technical_issues', label: 'Technical Issues' },
      { value: 'duplicate_booking', label: 'Duplicate Booking' },
      { value: 'other', label: 'Other' }
    ];
  },

  /**
   * Get cancellation types options
   * @param {string} userType - Type of user ('admin', 'service_provider', 'patient')
   * @returns {Array} Array of cancellation type options
   */
  getCancellationTypes: (userType = 'admin') => {
    const allTypes = [
      { value: 'patient_cancelled', label: 'Patient Cancelled' },
      { value: 'admin_cancelled', label: 'Admin Cancelled' },
      { value: 'service_provider_cancelled', label: 'Service Provider Cancelled' },
      { value: 'system_cancelled', label: 'System Cancelled' },
      { value: 'doctor_cancelled', label: 'Doctor Cancelled' }
    ];

    // Filter based on user type
    switch (userType) {
      case 'admin':
        return allTypes.filter(type => 
          ['admin_cancelled', 'patient_cancelled', 'system_cancelled', 'doctor_cancelled'].includes(type.value)
        );
      case 'service_provider':
        return allTypes.filter(type => 
          ['service_provider_cancelled', 'patient_cancelled', 'doctor_cancelled'].includes(type.value)
        );
      case 'patient':
        return allTypes.filter(type => 
          ['patient_cancelled'].includes(type.value)
        );
      default:
        return allTypes;
    }
  },

  /**
   * Check if an appointment can be cancelled
   * @param {Object} appointment - Appointment object
   * @returns {boolean} Whether the appointment can be cancelled
   */
  canCancelAppointment: (appointment) => {
    return appointment && !['completed', 'cancelled'].includes(appointment.status);
  },

  /**
   * Get cancellation status display information
   * @param {Object} appointment - Appointment object
   * @returns {Object|null} Cancellation status information or null if not cancelled
   */
  getCancellationStatus: (appointment) => {
    if (appointment.status !== 'cancelled') {
      return null;
    }

    return {
      cancelledBy: appointment.cancelled_by_name,
      cancelledAt: appointment.cancelled_at,
      reason: appointment.cancellation_reason_display || appointment.cancellation_reason,
      type: appointment.cancellation_type_display || appointment.cancellation_type,
      notes: appointment.cancellation_notes
    };
  }
};

// Export individual cancellation functions
export const cancelAppointmentAdmin = async (appointmentId, cancellationData) => {
  try {
    const response = await apiClient.post(`/api/admin/appointments/${appointmentId}/cancel/`, cancellationData);
    return response.data;
  } catch (error) {
    console.error('Error cancelling appointment (Admin):', error);
    throw error;
  }
};

export const cancelAppointmentServiceProvider = async (appointmentId, cancellationData) => {
  try {
    const response = await apiClient.post(`/api/sp/appointments/${appointmentId}/cancel/`, cancellationData);
    return response.data;
  } catch (error) {
    console.error('Error cancelling appointment (Service Provider):', error);
    throw error;
  }
};

export const cancelAppointmentEasyOP = async (appointmentId, cancellationData) => {
  try {
    const response = await apiClient.post(`/api/sp/easyop-appointments/${appointmentId}/cancel/`, cancellationData);
    return response.data;
  } catch (error) {
    console.error('Error cancelling EasyOP appointment:', error);
    throw error;
  }
};

// Unified cancellation function that automatically detects user type and appointment type
export const cancelAppointment = async (appointmentId, cancellationData, userType = 'admin', appointmentType = 'regular') => {
  try {
    let response;
    
    switch (userType) {
      case 'admin':
        response = await cancelAppointmentAdmin(appointmentId, cancellationData);
        break;
      case 'service_provider':
        if (appointmentType === 'easyop') {
          response = await cancelAppointmentEasyOP(appointmentId, cancellationData);
        } else {
          response = await cancelAppointmentServiceProvider(appointmentId, cancellationData);
        }
        break;
      case 'patient':
        // For patients, we'll use the service provider endpoint for now
        // In the future, this could be a dedicated patient endpoint
        response = await cancelAppointmentServiceProvider(appointmentId, cancellationData);
        break;
      default:
        throw new Error(`Unsupported user type: ${userType}`);
    }
    
    return response;
  } catch (error) {
    console.error(`Error cancelling appointment (${userType}):`, error);
    throw error;
  }
};

export const getCancellationReasons = () => {
  return [
    { value: 'patient_requested', label: 'Patient Requested' },
    { value: 'doctor_unavailable', label: 'Doctor Unavailable' },
    { value: 'emergency', label: 'Emergency' },
    { value: 'weather_conditions', label: 'Weather Conditions' },
    { value: 'technical_issues', label: 'Technical Issues' },
    { value: 'duplicate_booking', label: 'Duplicate Booking' },
    { value: 'doctor_sick', label: 'Doctor Sick' },
    { value: 'doctor_equipment_issue', label: 'Doctor Equipment Issue' },
    { value: 'other', label: 'Other' }
  ];
};

export const getCancellationTypes = (userType = 'admin') => {
  const allTypes = [
    { value: 'patient_cancelled', label: 'Patient Cancelled' },
    { value: 'admin_cancelled', label: 'Admin Cancelled' },
    { value: 'service_provider_cancelled', label: 'Service Provider Cancelled' },
    { value: 'system_cancelled', label: 'System Cancelled' },
    { value: 'doctor_cancelled', label: 'Doctor Cancelled' }
  ];

  // Filter based on user type
  switch (userType) {
    case 'admin':
      return allTypes.filter(type => 
        ['admin_cancelled', 'patient_cancelled', 'system_cancelled', 'doctor_cancelled'].includes(type.value)
      );
    case 'service_provider':
      return allTypes.filter(type => 
        ['service_provider_cancelled', 'patient_cancelled', 'doctor_cancelled'].includes(type.value)
      );
    case 'patient':
      return allTypes.filter(type => 
        ['patient_cancelled'].includes(type.value)
      );
    default:
      return allTypes;
  }
};

export const canCancelAppointment = (appointment) => {
  return appointment && !['completed', 'cancelled'].includes(appointment.status);
};

export const getCancellationStatus = (appointment) => {
  if (appointment.status !== 'cancelled') {
    return null;
  }

  return {
    cancelledBy: appointment.cancelled_by_name,
    cancelledAt: appointment.cancelled_at,
    reason: appointment.cancellation_reason_display || appointment.cancellation_reason,
    type: appointment.cancellation_type_display || appointment.cancellation_type,
    notes: appointment.cancellation_notes
  };
};

// Utility functions for cancellation
export const createCancellationData = (reason, notes = '') => {
  return {
    cancellation_reason: reason,
    cancellation_notes: notes
  };
};

export const validateCancellationData = (cancellationData) => {
  const errors = [];
  
  if (!cancellationData.cancellation_reason) {
    errors.push('Cancellation reason is required');
  }
  
  if (cancellationData.cancellation_notes && cancellationData.cancellation_notes.length > 500) {
    errors.push('Cancellation notes cannot exceed 500 characters');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

export const getCancellationReasonsForUserType = (userType = 'admin') => {
  const allReasons = getCancellationReasons();
  
  switch (userType) {
    case 'admin':
      return allReasons; // Admin can use all reasons
    case 'service_provider':
      return allReasons.filter(reason => 
        !['patient_requested'].includes(reason.value) // SP can't cancel for patient request
      );
    case 'patient':
      return allReasons.filter(reason => 
        ['patient_requested', 'emergency', 'other'].includes(reason.value)
      );
    default:
      return allReasons;
  }
};

// ===============================
// DOCTOR APIs - Cross Service Provider Management
// ===============================

export const crossProviderDoctorAPI = {
  // Get all doctors with basic info
  getDoctors: async (params = {}) => {
    try {
      const response = await apiClient.get('/api/doctors/doctors/', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching doctors:', error);
      throw error;
    }
  },

  // Get complete doctor details across all service providers
  getDoctorDetails: async (doctorId) => {
    try {
      const response = await apiClient.get(`/api/doctors/doctors/${doctorId}/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching doctor details:', error);
      throw error;
    }
  },

  // Get doctor dashboard with summary data
  getDoctorDashboard: async (doctorId) => {
    try {
      const response = await apiClient.get(`/api/doctors/doctors/${doctorId}/dashboard/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching doctor dashboard:', error);
      throw error;
    }
  },

  // Get all appointments for a doctor across all service providers
  getDoctorAppointments: async (doctorId, filters = {}) => {
    try {
      const response = await apiClient.get(`/api/doctors/doctors/${doctorId}/appointments/`, { params: filters });
      return response.data;
    } catch (error) {
      console.error('Error fetching doctor appointments:', error);
      throw error;
    }
  },

  // Get all time slots for a doctor across all service providers
  getDoctorTimeSlots: async (doctorId, filters = {}) => {
    try {
      const response = await apiClient.get(`/api/doctors/doctors/${doctorId}/time-slots/`, { params: filters });
      return response.data;
    } catch (error) {
      console.error('Error fetching doctor time slots:', error);
      throw error;
    }
  },

  // Get all service providers where a doctor works
  getDoctorServiceProviders: async (doctorId) => {
    try {
      const response = await apiClient.get(`/api/doctors/doctors/${doctorId}/service-providers/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching doctor service providers:', error);
      throw error;
    }
  },

  // ===============================
  // EMAIL-BASED DOCTOR APIs
  // ===============================

  // Get doctor details by email
  getDoctorByEmail: async (email) => {
    try {
      const response = await apiClient.get(`/api/doctors/doctors/email/${email}/`);
      return response; // Return full response object
    } catch (error) {
      console.error('Error fetching doctor by email:', error);
      throw error;
    }
  },

  // Get doctor dashboard by email
  getDoctorDashboardByEmail: async (email) => {
    try {
      const response = await apiClient.get(`/api/doctors/doctors/email/${email}/dashboard/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching doctor dashboard by email:', error);
      throw error;
    }
  },

  // Get all appointments for a doctor by email
  getDoctorAppointmentsByEmail: async (email, filters = {}) => {
    try {
      const response = await apiClient.get(`/api/doctors/doctors/email/${email}/appointments/`, { params: filters });
      return response; // Return full response object
    } catch (error) {
      console.error('Error fetching doctor appointments by email:', error);
      throw error;
    }
  },

  // Get all time slots for a doctor by email
  getDoctorTimeSlotsByEmail: async (email, filters = {}) => {
    try {
      const response = await apiClient.get(`/api/doctors/doctors/email/${email}/time-slots/`, { params: filters });
      return response; // Return full response object
    } catch (error) {
      console.error('Error fetching doctor time slots by email:', error);
      throw error;
    }
  },

  // Get all service providers where a doctor works by email
  getDoctorServiceProvidersByEmail: async (email) => {
    try {
      const response = await apiClient.get(`/api/doctors/doctors/email/${email}/service-providers/`);
      return response; // Return full response object
    } catch (error) {
      console.error('Error fetching doctor service providers by email:', error);
      throw error;
    }
  },

  // ===============================
  // DOCTOR SEARCH APIs
  // ===============================

  // Search doctors by email
  searchDoctorsByEmail: async (email) => {
    try {
      const response = await apiClient.get('/api/doctors/doctors/search/by-email/', { params: { email } });
      return response.data;
    } catch (error) {
      console.error('Error searching doctors by email:', error);
      throw error;
    }
  },

  // Search doctors by phone
  searchDoctorsByPhone: async (phone) => {
    try {
      const response = await apiClient.get('/api/doctors/doctors/search/by-phone/', { params: { phone } });
      return response.data;
    } catch (error) {
      console.error('Error searching doctors by phone:', error);
      throw error;
    }
  },

  // ===============================
  // DOCTOR MANAGEMENT APIs
  // ===============================

  // Create a new doctor
  createDoctor: async (formData) => {
    try {
      const response = await apiClient.post('/api/doctors/doctors/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error creating doctor:', error);
      throw error;
    }
  },

  // Update doctor details
  updateDoctor: async (doctorId, formData) => {
    try {
      const response = await apiClient.patch(`/api/doctors/doctors/${doctorId}/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error updating doctor:', error);
      throw error;
    }
  },

  // Delete doctor
  deleteDoctor: async (doctorId) => {
    try {
      const response = await apiClient.delete(`/api/doctors/doctors/${doctorId}/`);
      return response.data;
    } catch (error) {
      console.error('Error deleting doctor:', error);
      throw error;
    }
  },

  // ===============================
  // UTILITY FUNCTIONS
  // ===============================

  // Get doctor statistics across all service providers
  getDoctorStatistics: async (doctorId) => {
    try {
      const dashboard = await crossProviderDoctorAPI.getDoctorDashboard(doctorId);
      return {
        totalServiceProviders: dashboard.total_service_providers,
        totalAppointments: dashboard.total_appointments,
        totalTimeSlots: dashboard.total_time_slots,
        pendingAppointments: dashboard.pending_appointments,
        confirmedAppointments: dashboard.confirmed_appointments,
        completedAppointments: dashboard.completed_appointments
      };
    } catch (error) {
      console.error('Error fetching doctor statistics:', error);
      throw error;
    }
  },

  // Get doctor statistics by email
  getDoctorStatisticsByEmail: async (email) => {
    try {
      const dashboard = await crossProviderDoctorAPI.getDoctorDashboardByEmail(email);
      return {
        totalServiceProviders: dashboard.total_service_providers,
        totalAppointments: dashboard.total_appointments,
        totalTimeSlots: dashboard.total_time_slots,
        pendingAppointments: dashboard.pending_appointments,
        confirmedAppointments: dashboard.confirmed_appointments,
        completedAppointments: dashboard.completed_appointments
      };
    } catch (error) {
      console.error('Error fetching doctor statistics by email:', error);
      throw error;
    }
  },

  // Check if doctor exists across multiple service providers
  checkDoctorExists: async (email) => {
    try {
      const doctors = await crossProviderDoctorAPI.searchDoctorsByEmail(email);
      return doctors.length > 0;
    } catch (error) {
      console.error('Error checking doctor existence:', error);
      return false;
    }
  },

  // Get doctor's upcoming appointments across all service providers
  getUpcomingAppointments: async (doctorId, limit = 5) => {
    try {
      const dashboard = await crossProviderDoctorAPI.getDoctorDashboard(doctorId);
      return dashboard.upcoming_appointments.slice(0, limit);
    } catch (error) {
      console.error('Error fetching upcoming appointments:', error);
      throw error;
    }
  },

  // Get doctor's recent appointments across all service providers
  getRecentAppointments: async (doctorId, limit = 5) => {
    try {
      const dashboard = await crossProviderDoctorAPI.getDoctorDashboard(doctorId);
      return dashboard.recent_appointments.slice(0, limit);
    } catch (error) {
      console.error('Error fetching recent appointments:', error);
      throw error;
    }
  }
};
